import classnames from 'classnames';
import ProductRankBadge from 'components/ProductRankBadge';
import StrapiImg from 'components/StrapiImg';
import WebpBlogImg from 'components/WebpBlogImg';
import WebpImg from 'components/WebpImg';
import dynamic from 'next/dynamic';

import _ from 'lodash';
import { DateTime } from 'luxon';
import Markdown from 'markdown-to-jsx';
import {
  BreadcrumbJsonLd,
  FAQPageJsonLd,
  NextSeo,
  ProductJsonLd,
} from 'next-seo';
import { useRouter } from 'next/router';
import React from 'react';

import useSWR from 'swr';
import { getProductWebpImageUrl } from 'utils';

import { useAppLaunch } from 'contexts/AppLaunch';
import { useLaunchData } from 'contexts/LaunchData';
import { useUserAuth } from 'contexts/UserAuth';
import api, { swrFetcher } from 'utils/api';
import * as cacheService from 'utils/cacheService';

import nextSeoConfig from 'utils/next-seo.config';
import { swrSorraAPIFetcher } from 'utils/wpApi';

import MaxWidthContainer from 'components/MaxWidthContainer';
import Menubar from 'components/Menubar';
import NominateProduct from 'components/NominateProduct';
import ProductPhotoCard from 'components/ProductPhotoCard';
import ProductSummary from 'components/ProductSummary';

import AdUnit from 'components/AdUnit';
import ReviewMetrics from 'components/ReviewMetrics';
import SorraButton from 'components/SorraButton';

import { firebaseAuth } from 'utils/firebaseConfig';

import { getRatingScoreEmoji, getRatingScoreText } from 'utils';

import useInView from 'hooks/useInView';
import { usePathname } from 'next/navigation';
import { getFormattedProductImageUrl } from 'utils';
import { swrGoFetcher } from 'utils/go_api';
import { MixpanelConfig } from 'utils/mixpanelConfig';
import redirectToLogin from 'utils/redirectToLogin';

import { keywordToFilters } from 'utils/keywordFilters';
import { getAgeRange, getGenderName } from 'utils/staticContent';
import { getProductOverallRanks } from 'utils/voting2025';

const findSingleKeyword = (field, id) => {
  if (!id) return null;
  for (const [key, val] of Object.entries(keywordToFilters)) {
    const others = Object.keys(val).filter(
      (k) =>
        k !== field &&
        k !== 'seo_keyword' &&
        k !== 'seo_description' &&
        k !== 'alias'
    );
    if (val[field]?.includes(id) && others.length === 0) return key;
  }
  return null;
};

const buildCategoryUrl = (keyword, categoryName) => {
  if (!keyword || !categoryName) return null;
  return `/category/${encodeURIComponent(keyword + categoryName)}`;
};

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// import CommentPopup from 'components/CommentPopup';
// import Footer from 'components/Footer';
// import MobileTabBar from 'components/MobileTabBar';
const CommentPopup = dynamic(() => import('components/CommentPopup'), {
  ssr: false,
});

const Footer = dynamic(() => import('components/Footer'), {
  ssr: false,
  // loading: () => <div className="h-32 animate-pulse bg-gray-100" />,
});

const MobileTabBar = dynamic(() => import('components/MobileTabBar'), {
  ssr: false,
});

const ProductReportModal = dynamic(
  () => import('components/ProductReportModal'),
  {
    ssr: false,
  }
);

const { useState, useEffect, useRef } = React;

// 區塊標題滑到頂欄下方多少距離內，就算「當前區塊」
const SECTION_ACTIVE_TOLERANCE = 120;
// const voting2024RankMap = _.keyBy(voting2024Ranks, 'key');

const LoadingSkeleton = () => {
  return (
    <MaxWidthContainer className="px-4">
      <div className="flex gap-x-12">
        <div className="w-full shrink-0 lg:w-[516px]">
          <div className="aspect-square w-full">
            <Skeleton height="100%" />
          </div>
          <div className="mt-6 flex h-[64px] gap-x-4 lg:h-[112px] lg:gap-x-6">
            <Skeleton height="100%" className="aspect-square" />
            <Skeleton height="100%" className="aspect-square" />
            <Skeleton height="100%" className="aspect-square" />
          </div>
        </div>
        <div className="hidden grow lg:block">
          <Skeleton height={25} width={397} />
          <Skeleton height={25} width={397} className="mt-3" />
          <Skeleton height={25} className="mt-8" />
          <Skeleton height={25} className="mt-3" />
          <Skeleton height={25} className="mt-3" />
          <Skeleton height={25} className="mt-3" />
        </div>
      </div>
      <div className="mt-16 flex w-full gap-x-4 overflow-hidden border-b border-sorra-gray-100 pb-3.5 lg:mt-[64px] lg:gap-x-6 lg:pb-6">
        <Skeleton width={164} className="h-5 lg:h-10" />
        <Skeleton width={109} className="h-5 lg:h-10" />
        <Skeleton width={109} className="h-5 lg:h-10" />
        <Skeleton width={109} className="h-5 lg:h-10" />
        <Skeleton width={109} className="h-5 lg:h-10" />
        <Skeleton width={109} className="h-5 lg:h-10" />
      </div>
      <div className="mt-5 flex gap-x-16 lg:mt-11">
        <div className="hidden lg:block">
          <Skeleton width={324} height={249} />
        </div>
        <div className="hidden grow lg:block">
          <div className="flex gap-x-4">
            <Skeleton width={80} height={40} borderRadius={40} />
            <Skeleton width={80} height={40} borderRadius={40} />
            <Skeleton width={80} height={40} borderRadius={40} />
            <Skeleton width={80} height={40} borderRadius={40} />
          </div>
          <Skeleton height={170} className="mt-9" />
          <Skeleton height={73} className="mt-5" />
          <Skeleton height={170} className="mt-6" />
        </div>
        <div className="block grow lg:hidden">
          <Skeleton width={144} height={30} />
          <Skeleton height={242} className="mt-5" />
          <div className="mt-6 flex gap-x-4 overflow-x-hidden">
            <Skeleton width={72} height={40} borderRadius={40} />
            <Skeleton width={72} height={40} borderRadius={40} />
            <Skeleton width={72} height={40} borderRadius={40} />
            <Skeleton width={72} height={40} borderRadius={40} />
          </div>
          <Skeleton height={170} className="mt-6" />
          <Skeleton height={66} className="mt-6" />
          <Skeleton height={170} className="mt-6" />
          <Skeleton height={48} className="mt-6" />
        </div>
      </div>
      <div className="hidden lg:block">
        <Skeleton width={164} height={40} className="mt-11" />
        <div className="mt-6 flex gap-x-6">
          {[0, 1, 2, 3].map((_, idx) => {
            return (
              <div key={idx} className="flex-1">
                <Skeleton height={170} />
                <Skeleton height={25} className="mt-6" />
                <Skeleton height={25} className="mt-3" />
              </div>
            );
          })}
        </div>
      </div>
    </MaxWidthContainer>
  );
};

function ItemCategory(props) {
  const { title, textColor, bgColor, onClick } = props;
  return (
    <div
      style={{ color: textColor, background: bgColor }}
      className={`cursor-pointer rounded-[6px] px-[8px] py-[4px] text-[12px] font-[500]`}
      onClick={onClick}
    >
      {title}
    </div>
  );
}

function SelectCategory(props) {
  const { notifications } = useLaunchData();
  const {
    isMobile,
    productReviewCount,
    arrInfluencerState,
    arrStoresState,
    summaryCount,
    testingDetail,
    testingOverview,
    premiumContent,
    commonQuestion,
    photoCount,
  } = props;

  const [selectedCategory, setSelectedCategory] = useState(0);
  const contentItems = [
    summaryCount > 0 ? { title: '產品摘要', key: 'product-summary' } : null,
    testingDetail?.length || testingOverview || premiumContent
      ? { title: `Sorra 小編實測結果`, key: 'testing-result' }
      : null,
    {
      title: '真實讀者評價 (' + productReviewCount + ')',
      key: 'product-comment',
    },
    // 沒有照片時整個區塊不渲染，tab 也要跟著不出現
    photoCount > 0
      ? { title: '真實照片 (' + photoCount + ')', key: 'product-photos' }
      : null,
    arrInfluencerState.length > 0
      ? { title: '其他評論', key: 'influencer-comment' }
      : null,
    { title: `成份分析`, key: 'product-analysis' },
    !!arrStoresState && arrStoresState.length > 0
      ? { title: '價格比較', key: 'price-compare' }
      : null,
    { title: '官方介紹', key: 'more-info' },
    { title: '款式', key: 'product-types' },
    { title: '常見問題', key: 'product-question' },
    { title: '相似產品', key: 'similar-products' },
    { title: '相關文章', key: 'related-article' },
  ];

  // 頂欄是 sticky 的，實際高度 = sticky top + 自己的 padding + 內容，
  // 桌機約 167 而不是硬寫的 100。直接量它的底線，捲動定位與高亮判斷才會一致。
  function getTopBarBottom() {
    const bar = document.getElementById('top-bar');
    if (bar) return bar.getBoundingClientRect().bottom;
    return isMobile ? 177 : 100;
  }

  function pressItem(index, key) {
    setSelectedCategory(index);
    const target = document.getElementById(key);
    if (target) {
      document.querySelector('#app-wrapper').scrollTo({
        top: target.offsetTop - getTopBarBottom(),
        behavior: 'smooth',
      });
    }
  }

  // 高亮哪一顆，直接量「實際渲染出來的 tab」對應的區塊位置。
  // 舊版另外維護一份硬寫的 8 個區塊清單，但 contentItems 是條件組出來的
  // （產品摘要／實測結果／其他評論／價格比較都可能不存在），
  // 兩份清單的索引對不起來，高亮就會落在錯的 tab 上。
  const sectionKeysRef = useRef(_.map(_.compact(contentItems), 'key'));
  sectionKeysRef.current = _.map(_.compact(contentItems), 'key');

  useEffect(() => {
    const handleScroll = () => {
      try {
        // 標題剛滑到頂欄下方就該算當前區塊了，所以基準線再往下放一點；
        // 只用頂欄底線的話，標題明明就在畫面最上方卻還高亮前一顆。
        const line = getTopBarBottom() + SECTION_ACTIVE_TOLERANCE;
        let active = 0;
        sectionKeysRef.current.forEach((key, index) => {
          const el = document.getElementById(key);
          if (!el) return;
          // 取最後一個越過基準線的，中間有區塊還沒 lazy-load 出來也不會算錯
          if (el.getBoundingClientRect().top <= line) {
            active = index;
          }
        });
        setSelectedCategory(active);
      } catch (e) {}
    };

    if (document.querySelector('#app-wrapper')) {
      document
        .querySelector('#app-wrapper')
        .addEventListener('scroll', handleScroll);
    }
    return () => {
      if (document.querySelector('#app-wrapper')) {
        document
          .querySelector('#app-wrapper')
          .removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <div
      style={{ top: notifications.length > 0 ? '95px' : '50px' }}
      className="sticky z-[3] w-screen bg-[#fff] pb-0 pt-[37px] lg:mx-0 lg:pt-[77px]"
      id="top-bar"
    >
      <MaxWidthContainer>
        <div className="mx-[16px] flex cursor-pointer flex-row items-center justify-start space-x-[24px] overflow-x-scroll lg:mx-0 lg:space-x-[32px]">
          {_.compact(contentItems).map((item, index) => {
            return (
              <div
                key={item.key}
                id={index}
                className="flex shrink-0 flex-col"
                onClick={() => {
                  pressItem(index, item.key);
                }}
              >
                <div
                  className={`${
                    selectedCategory === index
                      ? 'font-[700]'
                      : 'font-[400] text-[#4A4A4A]'
                  } text-[16px] lg:text-[20px]`}
                >
                  {item.title}
                </div>
                <div
                  className={`mt-[10px] h-[3px] ${
                    selectedCategory === index ? 'bg-sorra-pink' : ''
                  } rounded-[100px]`}
                />
              </div>
            );
          })}
        </div>
      </MaxWidthContainer>
      <div className="h-[1px] w-full bg-[#E7E7E7]" />
    </div>
  );
}

const ProductComments = (props) => {
  const {
    product,
    productReviewCountState,
    setIsCommentModalShow,
    setImageList,
    setSelectedImageIndex,
    setIsReviewPopupShow,
    setReviewUser,
    countTotal,
    countPositive,
    countNeutral,
    countNegative,
    reviewAlt,
    hasProductPhotos,
  } = props;

  const router = useRouter();
  const { asPath, query } = router;
  const { itemID } = query;

  const { factorMap, reviewSourceMap, reviewTagMap } = useLaunchData();
  const { user } = useUserAuth();

  const [ratingState, setRatingState] = useState('-');
  const [ratingRateState, setRatingRateState] = useState('暫無');

  const productRating = product.rating;
  const productReviews = product.reviews || [];
  const productReviewFactorOverview = product.review_factor_overview || [];
  const productReviewCount = productRating?.total || 0;
  const secondCategoryIds = _.map(product.second_categories, 'id');

  useEffect(() => {
    if (productRating) {
      var rating = '';
      var ratingRate = '';
      var ratingPercentage =
        (productRating.positive / productRating.total) * 100;
      var ratingCount = productReviewCountState;
      var isRatingNeutral = productRating.neutral == productRating.total;

      if (ratingPercentage < 49) {
        rating += '🤔';
      } else if (ratingPercentage < 79) {
        rating += '👌';
      } else if (productReviewCount == 0) {
        rating = '';
      } else {
        rating += '❤️';
      }

      if (productReviewCount == 0) {
        rating += '-';
      } else {
        rating += ' ' + Math.round(ratingPercentage) + '% ';
        rating += '(' + ratingCount + ')';
      }

      if (productReviewCount == 0) {
        ratingRate += '暫無';
      } else if (ratingPercentage == 100) {
        ratingRate = '一致向好';
      } else if (ratingPercentage >= 60) {
        ratingRate += '大致向好';
      } else if (ratingPercentage >= 50) {
        ratingRate += '好壞參半';
      } else if (ratingPercentage >= 1) {
        ratingRate += '一般';
      } else if (isRatingNeutral) {
        ratingRate += '中性';
      } else {
        ratingRate += '一致向差';
      }

      setRatingState(rating);
      setRatingRateState(ratingRate);
    }
  }, [productRating]);

  return (
    <div className="mx-[16px] my-[24px] scroll-m-4 lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full" id="product-comment">
          <h2 className="text-2xl font-bold text-sorra-gray-900">
            {'真實讀者評價 (' + productReviewCount + ')'}
          </h2>
          <div className="mt-[24px] block flex-row space-y-[24px] lg:flex lg:space-x-[64px] lg:space-y-[0px]">
            <div className="lg:w-[324px]">
              <div className="w-full rounded-lg border-[1px] border-[#9B9B9B] px-[16px] py-[24px]">
                <div className="w-full text-center text-sm text-sorra-gray-800 lg:text-left lg:text-lg">
                  評率: {ratingRateState}
                </div>
                <div className="mt-[4px] hidden w-full lg:flex">
                  <div className="text-2xl font-bold">{ratingState}</div>
                  <div className="flex grow items-end justify-end space-x-[16px]">
                    <div className="flex items-center space-x-[4px]">
                      <div className="h-[8px] w-[8px] rounded-full bg-[#F16B7C]"></div>
                      <div className="text-xs font-medium text-sorra-gray-900">
                        好評
                      </div>
                    </div>
                    <div className="flex items-center space-x-[4px]">
                      <div className="h-[8px] w-[8px] rounded-full bg-[#FFA66D]"></div>
                      <div className="text-xs font-medium text-sorra-gray-900">
                        中性
                      </div>
                    </div>
                    <div className="flex items-center space-x-[4px]">
                      <div className="h-[8px] w-[8px] rounded-full bg-[#9B9B9B]"></div>
                      <div className="text-xs font-medium text-sorra-gray-900">
                        差評
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-[4px] block w-full lg:hidden">
                  <div className="text-center text-2xl font-bold">
                    {ratingState}
                  </div>
                  <div className="mt-[12px] flex items-center justify-center space-x-[16px]">
                    <div className="flex items-center space-x-[4px]">
                      <div className="h-[8px] w-[8px] rounded-full bg-[#F16B7C]"></div>
                      <div className="text-xs font-medium text-sorra-gray-900">
                        好評
                      </div>
                    </div>
                    <div className="flex items-center space-x-[4px]">
                      <div className="h-[8px] w-[8px] rounded-full bg-[#FFA66D]"></div>
                      <div className="text-xs font-medium text-sorra-gray-900">
                        中性
                      </div>
                    </div>
                    <div className="flex items-center space-x-[4px]">
                      <div className="h-[8px] w-[8px] rounded-full bg-[#9B9B9B]"></div>
                      <div className="text-xs font-medium text-sorra-gray-900">
                        差評
                      </div>
                    </div>
                  </div>
                </div>
                {/* <div className="mt-[32px] space-y-[12px]">{blockBarState}</div> */}
                <div className="mt-[32px] space-y-[12px]">
                  {productReviewFactorOverview.length > 0
                    ? productReviewFactorOverview.map((item, index) => {
                        var totalCount =
                          item.overview[0] +
                          item.overview[1] +
                          item.overview[-1];
                        return (
                          <div
                            key={'review ' + item.id + '-' + index}
                            className="flex items-center space-x-[16px]"
                          >
                            <div className="w-[36px] text-right text-xs text-sorra-gray-800">
                              {item.name}
                            </div>
                            <div className="flex h-[8px] grow overflow-hidden rounded-full">
                              <div
                                className={'h-full bg-[#D9D9D9]'}
                                style={{
                                  width: (totalCount == 0 ? 1 : 0) * 100 + '%',
                                }}
                              ></div>
                              <div
                                className={'h-full bg-[#F16B7C]'}
                                style={{
                                  width:
                                    (totalCount == 0
                                      ? 0
                                      : item.overview[1] / totalCount) *
                                      100 +
                                    '%',
                                }}
                              ></div>
                              <div
                                className={'h-full bg-[#FFA66D]'}
                                style={{
                                  width:
                                    (totalCount == 0
                                      ? 0
                                      : item.overview[0] / totalCount) *
                                      100 +
                                    '%',
                                }}
                              ></div>
                              <div
                                className={'h-full bg-[#9B9B9B]'}
                                style={{
                                  width:
                                    (totalCount == 0
                                      ? 0
                                      : item.overview[-1] / totalCount) *
                                      100 +
                                    '%',
                                }}
                              ></div>
                            </div>
                          </div>
                        );
                      })
                    : ['混合肌', '乾肌', '油肌', '中性肌'].map((item) => {
                        return (
                          <div
                            key={item}
                            className="flex items-center space-x-[16px]"
                          >
                            <div className="w-[36px] text-right text-xs text-sorra-gray-800">
                              {item}
                            </div>
                            <div className="flex h-[8px] grow overflow-hidden rounded-full">
                              <div
                                className={'h-full bg-[#D9D9D9]'}
                                style={{ width: '100%' }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                </div>
              </div>
            </div>
            {productReviews.length > 0 ? (
              <div className="grow space-y-[24px] lg:w-[calc(100%-324px-64px)]">
                <div className="mb-[32px] flex flex-wrap justify-start gap-x-[12px] gap-y-[10px] lg:gap-x-[16px]">
                  <div
                    className="cursor-pointer rounded-full bg-[#4A4A4A] px-[16px] py-[8px] text-base font-medium text-[#FFF]"
                    onClick={() => {
                      window.location = '/product/reviews/' + itemID;
                    }}
                  >
                    全部 ({countTotal})
                  </div>
                  <div
                    className="cursor-pointer rounded-full bg-[#F5F5F5] px-[16px] py-[8px] text-base font-medium text-[#4A4A4A]"
                    onClick={() => {
                      window.location = '/product/reviews/' + itemID;
                    }}
                  >
                    ❤️好評 ({countPositive})
                  </div>
                  <div
                    className="cursor-pointer rounded-full bg-[#F5F5F5] px-[16px] py-[8px] text-base font-medium text-[#4A4A4A]"
                    onClick={() => {
                      window.location = '/product/reviews/' + itemID;
                    }}
                  >
                    👌中性 ({countNeutral})
                  </div>
                  <div
                    className="cursor-pointer rounded-full bg-[#F5F5F5] px-[16px] py-[8px] text-base font-medium text-[#4A4A4A]"
                    onClick={() => {
                      window.location = '/product/reviews/' + itemID;
                    }}
                  >
                    👿差評 ({countNegative})
                  </div>
                </div>
                {productReviews.slice(0, 3).map((review, idx) => {
                  const {
                    id,
                    comment,
                    mask_name,
                    score,
                    factors,
                    cert_images,
                    comparison_images,
                    sorra_user,
                    created_at,
                    review_metric_scores = [],
                  } = review;

                  var ageString = '';
                  var commentCountString = '';

                  const reviewFactor = _.head(factors);
                  const factor = factorMap[reviewFactor]?.name || '無註明';

                  const ageRange =
                    _.get(review, 'sorra_user.age_range', '') || '';

                  const birthYear =
                    _.get(review, 'sorra_user.birth_year', '') || '';

                  if (birthYear || ageRange) {
                    ageString += `| ${birthYear ? getAgeRange(birthYear).name : ageRange} 歲`;
                  }

                  const gender = _.get(review, 'sorra_user.gender', '');
                  const genderName = getGenderName(gender);
                  if (genderName) {
                    ageString += ` | ${genderName}`;
                  }

                  if (
                    sorra_user &&
                    !_.isNil(sorra_user.review_count) &&
                    sorra_user?.review_count > 0
                  ) {
                    commentCountString += `| ${sorra_user.review_count}則評價`;
                  }

                  const { is_from_brand: isFromBrand = false } = review;

                  const imageList = [
                    ...(cert_images || []),
                    ...(comparison_images || []),
                  ];

                  let rating = `${getRatingScoreEmoji(score)} ${getRatingScoreText(score)}`;

                  var commentString = comment;

                  let source = '';
                  if (review.source) {
                    switch (review.source) {
                      case 1:
                        source = 'Sorra官網';
                        break;
                      case 2:
                        source = 'Sorra IG社群';
                        break;
                    }
                  }

                  const createdAt = DateTime.fromISO(created_at);
                  const timeString = createdAt.toFormat('dd/MM/yyyy HH:mm');
                  return (
                    <React.Fragment key={id}>
                      <div className="w-full rounded-lg bg-[#F5F5F5] px-[16px] py-[24px] lg:px-[24px]">
                        <div
                          className={classnames(
                            'flex items-center gap-x-2',
                            sorra_user ? 'cursor-pointer' : ''
                          )}
                          onClick={() => {
                            if (sorra_user) {
                              router.push(`/p/${sorra_user.referral_code}`);
                            }
                          }}
                        >
                          <div className="h-[40px] w-[40px]">
                            {isFromBrand ? (
                              <div className="flex h-full w-full items-center justify-center rounded-full bg-[#D6DDFB]">
                                <StrapiImg
                                  src="/images/icons/brand-review.png"
                                  className="h-5 w-5 object-contain"
                                />
                              </div>
                            ) : sorra_user && sorra_user.picture ? (
                              <WebpImg
                                className="h-full w-full rounded-full object-cover"
                                src={sorra_user.picture}
                                width={40}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center rounded-full bg-[#D6DDFB80] text-sorra-blue">
                                {`${mask_name.charAt(0)}${mask_name.charAt(
                                  mask_name.length - 1
                                )}`}
                              </div>
                            )}
                          </div>
                          <div className="grow">
                            <div className="text-sm font-bold text-[#111928]">
                              {mask_name}
                            </div>
                            <div className="mt-1 text-xs text-sorra-gray-800">
                              {isFromBrand ? '' : !factor ? '無註明' : factor}{' '}
                              {ageString} {commentCountString}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-x-2 pt-4">
                          <span className="text-base font-bold text-[#111928]">
                            {rating}
                          </span>

                          {review.is_real_cert ? (
                            <div className="cursor-default rounded-md bg-[#D6DDFB80] px-2 py-1 text-[12px] text-sorra-blue">
                              真實用家認證
                            </div>
                          ) : null}

                          {review.activity ? (
                            <div className="cursor-default rounded-md bg-[#FFDCC680] px-2 py-1 text-[12px] text-[#FFA66D]">
                              試用品
                            </div>
                          ) : null}

                          {review.review_tags.map((item) => {
                            // const targetTag = arrReviewTagsState.find(
                            //   (tagItem) => item == tagItem.id
                            // );
                            const targetTag = reviewTagMap[item];
                            return (
                              <div
                                style={{
                                  color: targetTag.text_color,
                                  background: targetTag.background_color,
                                }}
                                className={`cursor-default rounded-md px-2 py-1 text-[12px]`}
                              >
                                {targetTag.name}
                              </div>
                            );
                          })}
                        </div>
                        <div
                          className="cursor-pointer"
                          onClick={() => {
                            router.push(
                              `/product/${itemID}/reviews/${review.id}`
                            );
                          }}
                        >
                          <div className="break-words pt-1 text-base text-[#111928]">
                            {/* {idx != 0 && !user ? (
                            <Skeleton width={100} height={18} />
                          ) : (
                            commentString
                          )}
                           */}
                            {commentString}
                          </div>
                          <ReviewMetrics
                            className="mt-2"
                            categoryIds={secondCategoryIds}
                            metrics={review_metric_scores}
                          />
                        </div>
                        {review.comparison_images.length > 0 ||
                        review.cert_images.length > 0 ? (
                          <div className="flex items-center space-x-2 pt-[16px]">
                            {review.cert_images.map((item, index) => (
                              <WebpImg
                                key={item.id}
                                alt={
                                  reviewAlt +
                                  '/實測照片 by ' +
                                  (sorra_user?.name || '') +
                                  ' ' +
                                  index
                                }
                                title={
                                  reviewAlt +
                                  '/用後感 by ' +
                                  (sorra_user?.name || '') +
                                  ' ' +
                                  index
                                }
                                src={getProductWebpImageUrl(item)}
                                className="h-[70px] w-[70px] cursor-pointer rounded object-cover"
                                onClick={() => {
                                  setImageList(imageList);
                                  setSelectedImageIndex(index);
                                  setIsReviewPopupShow(true);
                                  setReviewUser(sorra_user?.name || '');
                                }}
                                width={70}
                              />
                            ))}
                            {review.comparison_images.map((item, index) => (
                              <WebpImg
                                key={item.id}
                                alt={
                                  reviewAlt +
                                  '/實測照片 by ' +
                                  (sorra_user?.name || '') +
                                  ' ' +
                                  (review.cert_images.length + index)
                                }
                                title={
                                  reviewAlt +
                                  '/用後感 by ' +
                                  (sorra_user?.name || '') +
                                  ' ' +
                                  (review.cert_images.length + index)
                                }
                                src={getProductWebpImageUrl(item)}
                                className="h-[70px] w-[70px] cursor-pointer rounded object-cover"
                                onClick={() => {
                                  setImageList(imageList);
                                  setSelectedImageIndex(
                                    review.cert_images.length + index
                                  );
                                  setIsReviewPopupShow(true);
                                  setReviewUser(sorra_user?.name || '');
                                }}
                                width={70}
                              />
                            ))}
                          </div>
                        ) : null}
                        <div className="pt-[24px] text-xs text-[#4A4A4A]">
                          {timeString} 在 {source} 評價
                        </div>

                        {/* Comment-in-comment */}
                        <div
                          className="flex cursor-pointer items-center gap-x-1 pt-3 text-xs font-bold text-black"
                          onClick={() => {
                            if (!user) {
                              redirectToLogin(
                                `/product/reviews/${itemID}?reply=${review.id}`
                              );
                            } else {
                              window.location = `/product/reviews/${itemID}?reply=${review.id}`;
                            }
                          }}
                        >
                          <StrapiImg
                            className="h-[16px] w-[16px]"
                            src="/svgs/chat-black.svg"
                          ></StrapiImg>
                          <p>{`回應${review.reply_count == 0 ? '' : '(' + review.reply_count + ')'}`}</p>
                        </div>
                      </div>
                      {/* {idx === 0 && !user && (
                        <div
                          className="w-full rounded-lg px-[16px] py-[16px] lg:px-[24px]"
                          style={{
                            backgroundImage: `linear-gradient(to right, #FFF4E7, #FFEAEA`,
                          }}
                        >
                          <div className="flex">
                            <div className="flex grow items-center text-base font-medium text-[#111928]">
                              <StrapiImg
                                className="mr-[4px]"
                                src="/svgs/lock-open.svg"
                              ></StrapiImg>
                              成為會員來解鎖全部評價
                            </div>
                            <button
                              className="flex h-[41px] flex-row items-center justify-center rounded-[8px] bg-sorra-pink px-[20px] py-[10px] text-sm font-medium text-[#fff]"
                              onClick={() => {
                                MixpanelConfig.track(
                                  'signinup_from_product_contentblock'
                                );
                                redirectToLogin(asPath);
                              }}
                            >
                              成為會員
                            </button>
                          </div>
                        </div>
                      )} */}
                    </React.Fragment>
                  );
                })}
                {/* {blockCommentsState} */}
                <div className="p-none flex items-center justify-center pt-[32px] lg:p-4">
                  <a
                    type="button"
                    className="mb-2 mr-2 w-full rounded-lg border border-sorra-pink px-5 py-2.5 text-center text-sm font-medium text-sorra-pink focus:ring-0 focus:ring-blue-300 lg:w-[360px]"
                    href={'/product/reviews/' + itemID}
                  >
                    查看全部
                  </a>
                </div>
              </div>
            ) : (
              <div className="grow space-y-[24px]">
                <div
                  className="mt-[16px] w-auto rounded-lg px-[16px] py-[16px] lg:mt-0 lg:px-[24px]"
                  style={{
                    backgroundImage: `linear-gradient(to right, #FFF4E7, #FFEAEA`,
                  }}
                >
                  <div className="flex">
                    <div className="flex grow items-center text-base font-medium text-[#111928]">
                      <StrapiImg
                        className="mr-[4px]"
                        src="/svgs/lock-open.svg"
                      ></StrapiImg>
                      暫無留言～ 邀請你留下第一個評價！
                    </div>
                    <button
                      disabled={!user}
                      className={
                        'flex h-[41px] flex-row items-center justify-center rounded-[8px] px-[20px] py-[10px] text-sm font-medium text-[#fff]' +
                        (user ? ' bg-sorra-pink' : ' bg-[#FCD6DB]')
                      }
                      onClick={() => {
                        const auth = firebaseAuth;
                        const user = auth.currentUser;
                        if (!user) {
                          MixpanelConfig.track('signinup_from_product_comment');
                          redirectToLogin(asPath);
                        } else {
                          setIsCommentModalShow(true);
                        }
                      }}
                    >
                      留評價
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
};

const ProductPhotos = (props) => {
  const { photos, photoCount, itemID, productFullName, onOpen } = props;

  if (photos.length === 0) return null;

  return (
    <div className="mx-[16px] my-[24px] lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full">
          <h2 className="text-2xl font-bold text-sorra-gray-900">
            真實照片 ({photoCount || photos.length})
          </h2>
          <div className="mt-[24px] grid grid-cols-2 gap-[12px] lg:grid-cols-4 lg:gap-[24px]">
            {photos.map((photo, index) => (
              <ProductPhotoCard
                key={photo.photo_id}
                photo={photo}
                itemID={itemID}
                productFullName={productFullName}
                onOpen={() => onOpen(index)}
              />
            ))}
          </div>
          <div className="mt-[24px] flex items-center justify-center">
            <a
              type="button"
              className="flex h-12 w-full items-center justify-center rounded-lg border border-sorra-pink text-base font-medium text-sorra-pink focus:ring-0 lg:w-[360px]"
              href={`/product/${itemID}/photos`}
            >
              查看全部用戶實拍圖
            </a>
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
};

function InfluencerComments(props) {
  const { arrInfluencerState, blockInfluencerState, itemID } = props;

  return (
    <div
      className="mx-[16px] my-[24px] lg:mx-0 lg:my-[40px]"
      key={'influencer' + itemID}
    >
      <MaxWidthContainer>
        <div className="w-full" id="influencer-comment">
          <h2 className="text-2xl font-bold text-sorra-gray-900">其他評論</h2>
          <div className="mt-[24px] flex-row lg:flex">
            <div className="grid grid-cols-1 gap-4 gap-y-[32px] lg:grid-cols-4 lg:gap-y-[48px]">
              {blockInfluencerState}
            </div>
          </div>
          {arrInfluencerState.length > 0 ? (
            <div className="p-none mt-6 flex items-center justify-center lg:p-4">
              <a
                type="button"
                className="mb-2 mr-2 w-full rounded-lg border border-sorra-pink px-5 py-2.5 text-center text-sm font-medium text-sorra-pink focus:outline-none focus:ring-4 focus:ring-blue-300 lg:w-[360px]"
                href={'/product/influencers/' + itemID}
              >
                查看全部
              </a>
            </div>
          ) : null}
        </div>
      </MaxWidthContainer>
    </div>
  );
}

function ProductAnalysis(props) {
  const {
    ingridentCount,
    ingridentOverviewState,
    arrIngridentSafetyState,
    ingridentSafetyState,
    arrIngredientState,
    isUserLoggedIn,
    arrIngridentsFeatureState,
    hasIngredientProvider,
    ingredientProvider,
    approveIngredientProvision,
    requiresIngredientApproval,
    ingredientImageUrls = [],
    setImageList,
    setSelectedImageIndex,
    setIsReviewPopupShow,
  } = props;

  const mainIngrident = arrIngredientState[0];
  const targetSafety = arrIngridentSafetyState.find(
    (x) => x.id === ingridentOverviewState.safety
  );
  const safetyName = targetSafety?.name;
  var tagColor = '';

  const { user } = useUserAuth();
  const pathname = usePathname();
  const router = useRouter();
  const { asPath } = router;

  const EmptyState = ({
    hasIngredientProvider,
    requiresIngredientApproval,
    approveIngredientProvision,
  }) => (
    <div>
      <div className="grid grid-cols-12 items-center gap-4 px-[16px] py-[24px]">
        <div className="col-span-1">#1</div>
        <div className="col-span-3">
          <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
        </div>
        <div className="col-span-3">
          <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
        </div>
        <div className="col-span-2">
          <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
        </div>
        <div className="col-span-3">
          <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
        </div>
      </div>
      {requiresIngredientApproval && !approveIngredientProvision ? (
        <div
          className="flex w-[calc(100vw-32px)] items-center justify-center rounded-lg px-[16px] py-[24px] text-center text-sm font-medium lg:w-full"
          style={{
            background: 'linear-gradient(90deg, #FFF4E7 33%, #FFEAEA 100%)',
          }}
        >
          已有用戶提交此產品成分，資料正在驗證中
        </div>
      ) : (
        <div className="grid grid-cols-12 items-center gap-4 px-[16px] py-[24px]">
          <div className="col-span-1">#2</div>
          <div className="col-span-3">
            <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
          </div>
          <div className="col-span-3">
            <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
          </div>
          <div className="col-span-2">
            <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
          </div>
          <div className="col-span-3">
            <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
          </div>
        </div>
      )}
      <div className="grid grid-cols-12 items-center gap-4 px-[16px] py-[24px]">
        <div className="col-span-1">#3</div>
        <div className="col-span-3">
          <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
        </div>
        <div className="col-span-3">
          <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
        </div>
        <div className="col-span-2">
          <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
        </div>
        <div className="col-span-3">
          <div className="h-[18px] w-[100px] rounded bg-[#ebebeb]" />{' '}
        </div>
      </div>
      {!hasIngredientProvider && (
        <div
          style={{
            background: 'linear-gradient(90deg, #FFF4E7 0%, #FFEAEA 100%)',
          }}
          className="lef-0 absolute bottom-[72px] flex h-[72px] w-full items-center justify-center gap-x-1 bg-yellow-300 text-[#111928]"
        >
          <div className="h-4 w-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="100%"
              height="100%"
              viewBox="0 0 16 17"
              fill="none"
            >
              <path
                d="M3.33333 2.5V5.16667V2.5ZM2 3.83333H4.66667H2ZM4 11.8333V14.5V11.8333ZM2.66667 13.1667H5.33333H2.66667ZM8.66667 2.5L10.1907 7.07133L14 8.5L10.1907 9.92867L8.66667 14.5L7.14267 9.92867L3.33333 8.5L7.14267 7.07133L8.66667 2.5Z"
                stroke="#111928"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="mr-3 text-sm font-bold">
            有成份表在手？
            <br className="block lg:hidden" />
            首筆成功提交可獲 $10
          </p>
          <SorraButton
            onClick={() => {
              if (user) {
                // console.log('uuuuu');
                // console.log(window.location.pathname);
                const currentPath = window.location.pathname;
                router.push(`${currentPath}/ingredients`);
              } else {
                redirectToLogin(asPath);
              }
            }}
            disabled={!user?.available_provide_ingredient}
          >
            {user?.available_provide_ingredient ? '上傳成份' : '已達每日上限'}
          </SorraButton>
        </div>
      )}
    </div>
  );

  return (
    <div className="mx-[16px] my-[24px] scroll-m-4 lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full" id="product-analysis">
          <h2 className="text-2xl font-bold text-sorra-gray-900">{`成份分析`}</h2>
          <div className="mt-[24px] flex-row lg:flex lg:space-x-[64px]">
            <div className="lg:w-[324px]">
              <div className="w-full rounded-lg border-[1px] border-[#9B9B9B] px-[16px] py-[24px] text-[16px]">
                <div className="text-center text-[18px] text-[#4a4a4a] lg:text-left">
                  成份分析
                </div>
                <div className="mt-[4px] flex items-center justify-center text-[24px] font-[700] lg:justify-start">
                  <div
                    style={{ background: targetSafety?.tag_color ?? '#E7E7E7' }}
                    className={`mr-[8px] h-[8px] w-[8px] rounded-[50%]`}
                  />
                  <div>{safetyName ?? '未知'}</div>
                </div>
                <div className="mt-[24px] pl-[20px]">
                  {ingridentCount > 0 ? (
                    <>
                      <ul style={{ listStyleType: 'disc' }}>
                        <li>{`成份表包含${ingridentOverviewState?.count}個成分`}</li>
                        <li>{`濃度最高為 ${mainIngrident?.name}，一般作為溶劑，保濕`}</li>
                        <li>{`一般／風險成份：${ingridentOverviewState?.unsafe_count}個`}</li>
                      </ul>
                    </>
                  ) : (
                    <ul style={{ listStyleType: 'disc' }}>
                      <li>未找到成份資料</li>
                    </ul>
                  )}
                </div>
                {ingridentCount > 0 &&
                  ingredientImageUrls.filter(Boolean).length > 0 && (
                    <div className="mt-5">
                      <p className="mb-2 text-center text-[18px] text-[#4a4a4a] lg:text-left">
                        成份表圖片
                      </p>
                      <div className="flex flex-wrap justify-center gap-2 lg:justify-start">
                        {ingredientImageUrls
                          .filter(Boolean)
                          .map((url, idx, imgs) => (
                            <img
                              key={idx}
                              src={url}
                              alt={`成份表${idx + 1}`}
                              onClick={() => {
                                setImageList(imgs.map((u) => ({ url: u })));
                                setSelectedImageIndex(idx);
                                setIsReviewPopupShow(true);
                              }}
                              className="h-[70px] w-[70px] cursor-pointer rounded-lg object-cover"
                            />
                          ))}
                      </div>
                    </div>
                  )}
              </div>
              {ingridentCount > 0 &&
                hasIngredientProvider &&
                ingredientProvider && (
                  <div className="mt-5 text-sm text-gray-800">{`◎ 感謝用戶 ${ingredientProvider} 提供成份資料！詳細相關資料以實際產品標示為準。`}</div>
                )}
            </div>

            <div>
              <div
                className={classnames('relative', {
                  'overflow-scroll': arrIngredientState.length > 0,
                  'overflow-hidden': arrIngredientState.length === 0,
                })}
              >
                <div className="mt-[24px] min-w-[850px] lg:mt-0 lg:max-w-[950px] lg:grow">
                  <div className="grid grid-cols-12 gap-4 rounded-[8px] bg-[#4A4A4A] p-[16px] text-[16px] font-[400] text-[#fff] lg:w-full">
                    <div className="col-span-1">含量</div>
                    <div className="col-span-3">成分名稱</div>
                    <div className="col-span-3">成分中文名稱</div>
                    <div className="col-span-2">安全性</div>
                    <div className="col-span-3">一般用途</div>
                  </div>
                  {arrIngredientState.length === 0 ? (
                    <EmptyState
                      hasIngredientProvider={hasIngredientProvider}
                      requiresIngredientApproval={requiresIngredientApproval}
                      approveIngredientProvision={approveIngredientProvision}
                    />
                  ) : (
                    arrIngredientState.map((item, index) => {
                      const safety = arrIngridentSafetyState.find(
                        (x) => x.id === item.ingredient_safety
                      );
                      const safetyName = safety?.name || '-';
                      const features = item.features || [];

                      var blockIngridentTmp = [];

                      if (features.length > 0) {
                        features.map((feature) => {
                          blockIngridentTmp.push(
                            <div
                              key={feature}
                              className="rounded-[100px] bg-[#E7E7E7] px-[10px] py-[4px] text-[14px] text-[#76777B]"
                            >
                              {feature}
                            </div>
                          );
                        });
                      }

                      return (
                        <div
                          key={item.id}
                          className="grid grid-cols-12 items-center gap-4 px-[16px] py-[24px] odd:bg-[#F5F5F5]"
                        >
                          <div className="col-span-1">#{index + 1}</div>
                          <div className="col-span-3 break-words">
                            {item.has_link === 1 ? (
                              <a
                                href={`/ingredients/${item.id}-${item.name
                                  .toLowerCase()
                                  .trim()
                                  .replace(/\s+/g, '-')}`}
                                className="text-sorra-pink hover:underline"
                              >
                                {item.name}
                              </a>
                            ) : (
                              item.name
                            )}
                          </div>
                          <div className="col-span-3 break-words">
                            {item.detail}
                          </div>
                          <div className="col-span-2 flex flex-wrap items-center">
                            {safety ? (
                              <div
                                style={{ background: safety?.tag_color }}
                                className={`mr-[8px] h-[8px] w-[8px] rounded-[50%]`}
                              />
                            ) : null}
                            <div>{safetyName}</div>
                          </div>
                          <div className="col-span-3 flex flex-wrap items-center gap-2">
                            {blockIngridentTmp}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              {/* {!isUserLoggedIn && ingridentCount > 0 ? (
                <div
                  className="mt-[16px] w-auto rounded-lg px-[16px] py-[16px] lg:mt-0 lg:px-[24px]"
                  style={{
                    backgroundImage: `linear-gradient(to right, #FFF4E7, #FFEAEA`,
                  }}
                >
                  <div className="flex">
                    <div className="flex grow items-center text-base font-medium text-[#111928]">
                      <StrapiImg className="mr-[4px]" src="/svgs/lock-open.svg"></StrapiImg>
                      成為會員來解鎖成份分析
                    </div>
                    <button
                      className="flex h-[41px] flex-row items-center justify-center rounded-[8px] bg-sorra-pink px-[20px] py-[10px] text-sm font-medium text-[#fff]"
                      onClick={() => {
                        MixpanelConfig.track(
                          'signinup_from_product_contentblock'
                        );
                        redirectToLogin(asPath);
                      }}
                    >
                      成為會員
                    </button>
                  </div>
                </div>
              ) : null} */}
            </div>
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
}

const PriceCompare = (props) => {
  const { product } = props;

  const productStores = product.product_stores || [];

  const { storeMap } = useLaunchData();

  const [collapseState, setCollapseState] = useState(false);

  return (
    <div className="mx-[16px] my-[24px] lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full" id="price-compare">
          <div className="flex">
            <h2 className="grow text-2xl font-bold text-sorra-gray-900">
              價格比較
            </h2>
            <StrapiImg
              className="cursor-pointer"
              src={
                collapseState == true
                  ? '/svgs/index-section-expand.svg'
                  : '/svgs/index-section-collapse.svg'
              }
              onClick={() =>
                collapseState == true
                  ? setCollapseState(false)
                  : setCollapseState(true)
              }
            ></StrapiImg>
          </div>
          <div
            className={
              (collapseState == true ? 'hidden' : 'block') + ' pt-[8px]'
            }
          >
            {/* {blockStoresState} */}
            {productStores.map((productStore) => {
              const { id, url, price, capacity, store: storeId } = productStore;
              const store = storeMap[storeId];
              const storeName = store ? store.name : '';
              const storeUrl =
                store && store.image
                  ? store.image.url
                  : '/svgs/store-placeholder.svg';

              return (
                <div
                  key={id}
                  className={`border-b-[1px] border-gray-300 py-[16px] ${
                    url ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => {
                    if (url) {
                      window.open(url, '_blank');
                    }
                  }}
                >
                  <div className="flex w-full items-center p-[8px]">
                    <WebpImg
                      src={storeUrl}
                      width={80}
                      className="mr-[8px] h-[54px] w-[54px] lg:mr-[40px] lg:h-[80px] lg:w-[80px]"
                    ></WebpImg>
                    <div className="space-y-[2px] lg:space-y-[8px]">
                      <div className="text-base font-medium text-sorra-gray-900 lg:text-2xl">
                        {`${price ? 'HK $' + price : ''}${capacity ? '/' + capacity : ''}${storeName ? ' @ ' + storeName : ''}`}
                        {/* {'HK $' +
                          productStore.price +
                          (productStore.capacity != null
                            ? '/' + productStore.capacity
                            : '') +
                          ' @ ' +
                          storeName} */}
                      </div>
                      <div className="text-xs font-normal text-sorra-gray-800 lg:text-base">
                        {productStore.description}
                      </div>
                    </div>
                    <div
                      className=""
                      style={{ marginLeft: 'auto', marginRight: 0 + 'px' }}
                    >
                      {!_.isEmpty(url) ? (
                        <StrapiImg
                          className="h-[32px] w-[32px]"
                          src="/svgs/external-link.svg"
                        ></StrapiImg>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
};

function MoreInfo(props) {
  const { productDescriptionState } = props;
  const [collapseState, setCollapseState] = useState(false);
  return (
    <div className="mx-[16px] my-[24px] lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full" id="more-info">
          <div className="flex">
            <h2 className="grow text-2xl font-bold text-sorra-gray-900">
              官方介紹
            </h2>
            <StrapiImg
              className="cursor-pointer"
              src={
                collapseState == true
                  ? '/svgs/index-section-expand.svg'
                  : '/svgs/index-section-collapse.svg'
              }
              onClick={() =>
                collapseState == true
                  ? setCollapseState(false)
                  : setCollapseState(true)
              }
            ></StrapiImg>
          </div>
          <div
            className={
              (collapseState == true ? 'hidden' : 'block') + ' pt-[8px]'
            }
          >
            <div className="py-[16px]">
              {_.isEmpty(productDescriptionState) ? (
                '暫無內容'
              ) : (
                <Markdown>{productDescriptionState}</Markdown>
              )}
            </div>
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
}

function ProductTypes(props) {
  const { productVariationState } = props;

  const [collapseState, setCollapseState] = useState(false);
  const [infoMarkdown, setInfoMarkdown] = useState('');

  useEffect(() => {
    if (productVariationState != null) {
      setInfoMarkdown(<Markdown>{productVariationState}</Markdown>);
    }

    if (productVariationState == 'N/A') {
      setInfoMarkdown('暫無內容');
    }
  }, [productVariationState]);

  return (
    <div className="mx-[16px] my-[24px] lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full" id="product-types">
          <div className="flex">
            <h2 className="grow text-2xl font-bold text-sorra-gray-900">
              款式
            </h2>
            <StrapiImg
              className="cursor-pointer"
              src={
                collapseState == true
                  ? '/svgs/index-section-expand.svg'
                  : '/svgs/index-section-collapse.svg'
              }
              onClick={() =>
                collapseState == true
                  ? setCollapseState(false)
                  : setCollapseState(true)
              }
            ></StrapiImg>
          </div>
          <div
            className={
              (collapseState == true ? 'hidden' : 'block') + ' pt-[8px]'
            }
          >
            <div className="py-[16px]">{infoMarkdown}</div>
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
}

function Question(props) {
  const { commonQuestion } = props;
  const [questionMarkdown, setQuestionMarkdown] = useState([]);
  const [expandedItems, setExpandedItems] = useState({});

  // 切換特定項目的顯示狀態
  const toggleItem = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  useEffect(() => {
    if (commonQuestion != null) {
      setQuestionMarkdown(commonQuestion);
    } else {
      setQuestionMarkdown([]);
    }
  }, [commonQuestion]);

  return (
    <div className="mx-[16px] my-[24px] lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full" id="product-question">
          <div className="flex">
            <div className="grow text-2xl font-bold text-sorra-gray-900">
              常見問題
            </div>
          </div>
          <div className={'pt-[8px]'}>
            <div className="gap-2 py-[16px]">
              {questionMarkdown?.length > 0 &&
                questionMarkdown.map((q, index) => {
                  const isExpanded = expandedItems[index] || false;
                  return (
                    <div className={'mb-4 rounded-lg bg-[#F5F5F5]'} key={index}>
                      <div className="flex px-4 py-4">
                        <h2 className="font-notoSansTC grow text-[16px] font-semibold lg:text-[20px]">
                          {index + 1}. <Markdown>{q?.question}</Markdown>
                        </h2>
                        <StrapiImg
                          className="cursor-pointer"
                          src={
                            isExpanded == false
                              ? '/svgs/index-section-expand.svg'
                              : '/svgs/index-section-collapse.svg'
                          }
                          onClick={() => toggleItem(index)}
                        ></StrapiImg>
                      </div>
                      <div
                        className={`px-4 py-2 text-[16px] ${isExpanded ? 'block' : 'hidden'}`}
                      >
                        <Markdown>{q?.answer}</Markdown>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
}

function TestingResult(props) {
  const { testingDetail, testingOverview, premiumContent } = props;
  const rating = _.get(testingOverview, 'rating', null);
  const description = _.get(testingOverview, 'description', null);
  const metrics = _.get(testingOverview, 'metrics', []);
  const factors = _.get(_.head(metrics), 'values', []);
  const factorList = factors.map((factor) => factor.factor);
  const [selectedFactor, setSelectedFactor] = useState('油性肌');
  const [collapseState, setCollapseState] = useState(false);
  return (
    <div className="my-[24px] lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full" id="testing-result">
          <div className="mx-[24px] flex pb-6 lg:mx-0">
            <h2 className="grow text-2xl font-bold text-sorra-gray-900">
              Sorra 小編實測結果
            </h2>
          </div>
          <div className="lg:flex lg:w-full">
            <div className="mx-[16px] lg:mx-0 lg:w-[324px]">
              {testingOverview && (
                <div className="w-full rounded-lg border-[1px] border-[#9B9B9B] px-[16px] py-[24px]">
                  <div className="w-full text-center text-[16px] text-sorra-gray-800 lg:text-center lg:text-lg">
                    綜合評分及使用感
                  </div>
                  <div className="text-medium my-2 w-full text-center text-[60px] font-extrabold text-sorra-pink">
                    {rating}
                  </div>
                  <div className="w-full text-center text-[16px]">
                    {description}
                  </div>
                  <div className="mb-4 flex flex-wrap items-center justify-center gap-1 pt-4 lg:gap-4">
                    {factors.length > 2 &&
                      factorList.map((factor) => (
                        <div
                          className={`w-[83px] cursor-pointer rounded-full py-3 text-center font-rubik text-[14px] font-semibold lg:w-[83px] lg:px-4 lg:text-[16px] ${selectedFactor == factor ? 'bg-sorra-pink text-white' : 'border border-sorra-pink bg-white text-sorra-pink'}`}
                          onClick={() => setSelectedFactor(factor)}
                        >
                          <span>{factor}</span>
                        </div>
                      ))}
                  </div>
                  <div className="my-4 text-center">
                    {factors.length > 2 &&
                      metrics.map((metric, i) => (
                        <div key={metric.id || metric.name}>
                          {metric.values.map(
                            (value, j) =>
                              selectedFactor == value.factor && (
                                <div
                                  key={`${metric.name}-${value.factor}`}
                                  className={`flex items-center ${i == 0 && j == 0 ? `` : `mt-2`}`}
                                >
                                  <p className={'flex-1 text-left text-[16px]'}>
                                    {metric.name}
                                  </p>
                                  <p
                                    className="min-w-[40px] shrink-0 whitespace-nowrap text-left text-4xl font-extrabold"
                                    style={{
                                      color: '#F16B7C',
                                      filter:
                                        'invert(55%) sepia(77%) saturate(1641%) hue-rotate(313deg) brightness(99%) contrast(92%)',
                                    }}
                                  >
                                    {value.value}
                                  </p>
                                </div>
                              )
                          )}
                        </div>
                      ))}
                  </div>
                  <div className="my-4 text-center">
                    {factors.length <= 2 &&
                      metrics.map((metric, i) => (
                        <div key={metric.id || metric.name}>
                          {metric.values.map((value, j) => (
                            <div
                              key={`${metric.name}-${value.factor}`}
                              className={`flex items-center ${i == 0 && j == 0 ? `` : `mt-2`}`}
                            >
                              <p className={'flex-1 text-left text-[16px]'}>
                                {metric.name}
                                {value.factor && `(${value.factor})`}
                              </p>
                              <p
                                className="min-w-[40px] shrink-0 whitespace-nowrap text-left text-4xl font-extrabold"
                                style={{
                                  color: '#F16B7C',
                                  filter:
                                    'invert(55%) sepia(77%) saturate(1641%) hue-rotate(313deg) brightness(99%) contrast(92%)',
                                }}
                              >
                                {value.value}
                              </p>
                            </div>
                          ))}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
            <div className="mx-[16px] mb-4 lg:mx-0 lg:w-[calc(100%-324px-64px)] lg:pl-[64px]">
              {/* 會員專屬內容 */}
              {premiumContent && (
                <div className="gap-6 lg:flex lg:pb-6">
                  <div
                    className="cursor-pointer rounded-lg py-6 lg:py-0"
                    onClick={() =>
                      window.open(premiumContent.button_url, '_blank')
                    }
                  >
                    <img
                      src={premiumContent.image?.url}
                      className="h-auto w-full rounded-lg object-center lg:h-[135px] lg:h-[196px] lg:w-[232px] lg:w-[343px] lg:object-cover"
                    />
                  </div>
                  <div className="">
                    <p className="text-[16px] text-sorra-gray-800">
                      會員專屬內容
                    </p>
                    <p className="pb-6 text-[24px]">{premiumContent.title}</p>
                    <button
                      className="group hidden h-[48px] w-[360px] items-center justify-center gap-3 rounded-md border border-sorra-pink bg-white px-4 py-2 text-sm text-sorra-pink shadow-sm hover:bg-[#FCE1E5] lg:inline-flex"
                      onClick={() =>
                        window.open(premiumContent.button_url, '_blank')
                      }
                    >
                      <span className="flex items-center gap-3">
                        <span className="text-[16px] font-medium">
                          {premiumContent.button_text}
                        </span>

                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          className="flex-shrink-0"
                        >
                          <path
                            d="M6 2.66667H2.66667C2.22464 2.66667 1.80072 2.84226 1.48816 3.15482C1.17559 3.46738 1 3.89131 1 4.33333V12.6667C1 13.1087 1.17559 13.5326 1.48816 13.8452C1.80072 14.1577 2.22464 14.3333 2.66667 14.3333H11C11.442 14.3333 11.866 14.1577 12.1785 13.8452C12.4911 13.5326 12.6667 13.1087 12.6667 12.6667V9.33333M9.33333 1H14.3333M14.3333 1V6M14.3333 1L6 9.33333"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </div>
              )}
              {/* 實測詳情 */}
              {testingDetail && (
                <div className="-mr-4 rounded-lg bg-[#FAFAFA] pb-6 lg:p-6">
                  <div className="flex">
                    <p className="grow pl-4 pt-4 text-[16px] font-bold lg:pl-0 lg:pt-0 lg:text-[24px]">
                      實測詳情
                    </p>
                    <StrapiImg
                      className="cursor-pointer pr-4 lg:pr-0"
                      src={
                        collapseState == true
                          ? '/svgs/index-section-expand.svg'
                          : '/svgs/index-section-collapse.svg'
                      }
                      onClick={() =>
                        collapseState == true
                          ? setCollapseState(false)
                          : setCollapseState(true)
                      }
                    ></StrapiImg>
                  </div>
                  <div className="overflow-x-auto lg:mx-0 lg:overflow-visible">
                    <div
                      className={
                        (collapseState == true ? 'hidden' : 'block') +
                        ` flex gap-x-6 gap-y-2 px-4 lg:ml-0 lg:flex-row lg:flex-wrap lg:justify-center lg:px-0 ${
                          testingDetail.length % 4 <= 2
                            ? 'lg:gap-x-12'
                            : 'lg:gap-x-6'
                        }`
                      }
                    >
                      {testingDetail.map((t, index) => (
                        <div
                          key={index}
                          className={`flex-shrink-0 flex-col items-center justify-center pt-6 ${testingDetail.length == 4 || (t.image?.width && t.image?.height && t.image.width / t.image.height > 2.5) ? 'lg:w-[370px]' : 'lg:w-[234px]'} ${t.image?.width && t.image?.height && t.image.width / t.image.height > 2.5 ? 'w-[355px]' : 'w-[234px]'}`}
                        >
                          <div className="flex min-h-[48px] w-full items-center justify-center text-center">
                            <p className="text-[16px] font-bold">
                              {!_.isEmpty(t.title) && (
                                <Markdown>{t.title}</Markdown>
                              )}
                            </p>
                          </div>
                          {/* 對比圖片 */}
                          <div className="flex items-center justify-center py-2">
                            <div className={`relative rounded-lg`}>
                              <div
                                className={`h-[132px] min-h-[132px] min-w-[234px] ${t.image?.width && t.image?.height && t.image.width / t.image.height > 2.5 ? 'w-[355px]' : 'w-[234px]'} overflow-hidden rounded-lg`}
                              >
                                <img
                                  src={t.image?.url}
                                  alt={`實測${index}`}
                                  className="h-full w-full rounded-lg object-cover object-center"
                                />
                              </div>

                              {/* 評分徽章 */}
                              {t.rating && (
                                <div
                                  className="z-1 absolute -bottom-2 -right-4 flex h-[36px] w-[36px] items-center justify-center rounded-full bg-sorra-pink text-[16px] font-bold text-white shadow-lg lg:h-[40px] lg:w-[40px] lg:text-[24px]"
                                  style={{ boxShadow: '0 0 0 2px white' }}
                                >
                                  {t.rating}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="mb-4 text-[16px] font-normal leading-relaxed text-gray-700">
                              {!_.isEmpty(t.description) && (
                                <Markdown>{t.description}</Markdown>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {premiumContent && (
              <div className="mx-auto flex justify-center px-4 pb-8">
                <button
                  className="group inline-flex h-[48px] w-[343px] items-center justify-center gap-3 rounded-md border border-sorra-pink bg-white px-4 py-2 text-sm text-sorra-pink shadow-sm hover:bg-[#FCE1E5] lg:hidden"
                  onClick={() =>
                    window.open(premiumContent.button_url, '_blank')
                  }
                >
                  <span className="flex items-center gap-3">
                    <span className="text-[16px] font-medium">
                      {premiumContent.button_text}
                    </span>

                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="flex-shrink-0"
                    >
                      <path
                        d="M6 2.66667H2.66667C2.22464 2.66667 1.80072 2.84226 1.48816 3.15482C1.17559 3.46738 1 3.89131 1 4.33333V12.6667C1 13.1087 1.17559 13.5326 1.48816 13.8452C1.80072 14.1577 2.22464 14.3333 2.66667 14.3333H11C11.442 14.3333 11.866 14.1577 12.1785 13.8452C12.4911 13.5326 12.6667 13.1087 12.6667 12.6667V9.33333M9.33333 1H14.3333M14.3333 1V6M14.3333 1L6 9.33333"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
}

const SimilarProducts = (props) => {
  const { product, isLoading } = props;

  const { storeMap } = useLaunchData();
  const relatedProducts = product.related_products || [];

  return (
    <div className="mx-[16px] my-[24px] lg:mx-0 lg:my-[40px]">
      <MaxWidthContainer>
        <div className="w-full" id="similar-products">
          <div className="flex items-center justify-between gap-6">
            <h2 className="text-2xl font-bold text-sorra-gray-900">相似產品</h2>
            <div className="hidden shrink-0 lg:block">
              <NominateProduct className="w-[380px]" />
            </div>
          </div>
          <div className="mt-[24px] flex flex-row overflow-x-scroll">
            <div className="flex w-full gap-4 gap-y-[48px] lg:grid lg:grid-cols-4">
              {isLoading
                ? _.map(new Array(8), (item, idx) => {
                    return (
                      <div
                        key={idx}
                        className="relative w-[217px] max-w-[1000px] cursor-pointer lg:w-auto"
                      >
                        <div className="aspect-square w-full overflow-hidden rounded-lg">
                          <Skeleton height="100%" width="100%"></Skeleton>
                        </div>
                        <div className="flex pt-6">
                          <div className="text-sm font-normal text-sorra-gray-800">
                            <Skeleton count={1} width={80}></Skeleton>
                          </div>
                          <div className="bottom-1 right-0 ml-[auto] text-sm font-bold">
                            <Skeleton count={1} width={40}></Skeleton>
                          </div>
                        </div>
                        <div className="pb-6 pt-1 text-lg font-medium text-sorra-gray-900">
                          <Skeleton count={2}></Skeleton>
                        </div>
                        <div className={'flex pb-1' + (true ? ' hidden' : '')}>
                          <div className="text-lg font-medium text-sorra-gray-900">
                            <Skeleton count={1} width={120}></Skeleton>
                          </div>
                          {!true ? (
                            <Skeleton
                              className="h-[28px] pl-1"
                              width={40}
                              height={28}
                            ></Skeleton>
                          ) : (
                            ''
                          )}
                        </div>
                        <div
                          className={
                            'pb-1 text-sm font-normal text-sorra-gray-800' +
                            (true ? ' hidden' : '')
                          }
                        >
                          <Skeleton count={1} width={100}></Skeleton>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                          {_.map(new Array(2), (item, idx) => (
                            <Skeleton
                              key={idx}
                              className="search-rightbar-content-tag-pink px-2 py-1 text-base font-medium"
                              width={80}
                              height={32}
                            ></Skeleton>
                          ))}
                        </div>
                      </div>
                    );
                  })
                : relatedProducts.map((relatedProduct, idx) => {
                    const {
                      id,
                      name,
                      en_name,
                      images,
                      brand,
                      ratings: rating,
                      stores: product_stores,
                      facts,
                    } = relatedProduct;
                    const firstProductStore = _.head(product_stores);
                    const store =
                      firstProductStore && storeMap[firstProductStore.store];
                    const storeUrl = store ? store.url : '';
                    const storeName = store ? store.name : '';
                    const price = firstProductStore
                      ? firstProductStore.price
                      : -1;
                    const productImage = _.head(images);
                    return (
                      <div
                        key={id}
                        className="relative w-[217px] max-w-[1000px] cursor-pointer lg:w-auto"
                        onClick={() => {
                          window.location.href = '/product/' + id;
                        }}
                      >
                        <div className="aspect-square w-full overflow-hidden rounded-lg border border-sorra-gray-100 bg-white">
                          <WebpImg
                            className="h-full w-full object-contain"
                            src={getProductWebpImageUrl(productImage)}
                            width={410}
                          ></WebpImg>
                        </div>
                        <div className="flex pt-6">
                          <div className="text-sm font-normal text-sorra-gray-800">
                            {brand ? brand.name : ''}
                          </div>
                          <div className="bottom-1 right-0 ml-[auto] text-sm font-bold">
                            {rating ? `👍 ${rating.rate}%` : '-'}
                          </div>
                        </div>
                        <div className="pb-6 pt-1 text-lg font-medium text-sorra-gray-900">
                          {name}
                          <br />
                          {en_name}
                        </div>
                        {store && (
                          <div
                            className={
                              'flex pb-1' + (price == -1 ? ' hidden' : '')
                            }
                          >
                            <div className="text-lg font-medium text-sorra-gray-900">
                              HK$ {price} @{' '}
                              {_.isEmpty(storeUrl) ? storeName : ''}
                            </div>
                            {!_.isEmpty(storeUrl) ? (
                              <WebpImg
                                className="h-[28px] pl-1"
                                src={storeUrl}
                                width={40}
                              ></WebpImg>
                            ) : (
                              ''
                            )}
                          </div>
                        )}
                        <div
                          className={
                            'pb-1 text-sm font-normal text-sorra-gray-800' +
                            ((product_stores || []).length - 1 <= 0
                              ? ' hidden'
                              : '')
                          }
                        >
                          還有{(product_stores || []).length - 1}間商戶亦有售
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
                          {(facts || []).map((fact, idx) => {
                            return (
                              <div
                                key={fact.id}
                                className="search-rightbar-content-tag-pink px-2 py-1 text-base font-medium"
                                style={{
                                  backgroundColor: fact.bg_color,
                                  color: fact.text_color,
                                }}
                              >
                                {fact.name}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>
          <div className="mt-6 lg:hidden">
            <NominateProduct className="w-full" />
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
};

function RelatedArticle(props) {
  const { posts, isLoading, itemID } = props;
  return (
    <div className="mx-[16px] my-[24px] gap-x-6 gap-y-6 px-4 lg:mx-0 lg:my-[40px] lg:grid-cols-4 lg:gap-y-0 lg:px-0">
      <MaxWidthContainer>
        <div className="w-full" id="related-article">
          <h2 className="text-2xl font-bold text-sorra-gray-900">相關文章</h2>
          <div className="mt-[24px] flex flex-row">
            <div className="grid w-full grid-cols-2 gap-4 lg:grid-cols-4">
              {isLoading
                ? _.map(new Array(4), (item, idx) => (
                    <div key={idx} className="block">
                      <div className="aspect-square w-full overflow-hidden rounded-xl">
                        <Skeleton height="100%" width="100%"></Skeleton>
                      </div>
                      <div className="pt-6 text-sm">
                        <Skeleton count={1}></Skeleton>
                      </div>
                      <div className="line-clamp-2 pt-1 text-lg font-medium">
                        <Skeleton count={2}></Skeleton>
                      </div>
                    </div>
                  ))
                : _.filter(posts, (post) => {
                    const ogImage = post.image_url?.url || '';
                    return ogImage != '' > 0;
                  }).map((posts, idx) => {
                    const { id, title, url, category_name, image_url } = posts;
                    const imageUrl = image_url?.url || '';
                    const categoryName = category_name || '';

                    return (
                      <a key={`${idx}:${id}`} className="block" href={url}>
                        <WebpBlogImg
                          className="aspect-square w-full rounded-xl object-cover"
                          src={imageUrl}
                        ></WebpBlogImg>
                        <div className="pt-6 text-sm text-sorra-gray-800">
                          {categoryName}
                        </div>
                        <div className="line-clamp-2 pt-1 text-lg font-medium text-sorra-gray-900">
                          {title.replaceAll('&amp;', '&')}
                        </div>
                      </a>
                    );
                  })}
            </div>
          </div>
          <div className="p-none flex items-center justify-center pt-[32px] lg:p-4">
            <a
              type="button"
              className="mb-2 mr-2 w-full rounded-lg border border-sorra-pink px-5 py-2.5 text-center text-sm font-medium text-sorra-pink focus:outline-none focus:ring-4 focus:ring-blue-300 lg:w-[360px]"
              href={'/product/blogs/' + itemID}
              target="_blank"
            >
              查看全部
            </a>
          </div>
        </div>
      </MaxWidthContainer>
    </div>
  );
}

function FactTagPopup(props) {
  const {
    isShow,
    setIsShow,
    productNameState,
    productBrandState,
    productImageState,
    arrProductFactsState,
    currentFactState,
    productFactsState,
  } = props;

  function ItemCategory(props) {
    const { title, textColor, bgColor } = props;
    return (
      <div
        style={{ color: textColor, background: bgColor }}
        className={`w-fit rounded-[6px] px-[8px] py-[4px] text-[12px] font-[500]`}
      >
        {title}
      </div>
    );
  }

  const closePopup = () => {
    setIsShow(false);
  };

  const currentFactDisplay = arrProductFactsState.find(
    (item) => item.id === currentFactState
  );
  const productFactsDisplay = productFactsState.find(
    (item) => item.id === currentFactState
  );
  const displayContent = currentFactDisplay?.items.filter((items) => {
    return productFactsDisplay?.valid_items.some((f) => {
      return f === items.id;
    });
  });

  if (!isShow) return null;

  return (
    <div className="register-popupbg hidden lg:flex" onClick={closePopup}>
      <div
        className="relative flex max-h-[670px] w-[480px] flex-col rounded-xl bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative mx-[16px] mt-[16px] flex h-[48px] flex-row items-center justify-between">
          <div className="flex items-center justify-center">
            <div
              className="absolute left-0 top-0 flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded-[8px] border-[1px] border-[#E7E7E7]"
              onClick={() => setIsShow(false)}
            >
              <StrapiImg src="/svgs/close.svg" className="h-[30px] w-[30px]" />
            </div>
          </div>
          <div />
        </div>
        <div className="mx-[16px] mb-[16px] mt-[36px] flex-grow overflow-scroll">
          <div className="flex items-center">
            <div className="flex h-[80px] w-[80px] items-center justify-center rounded-[16px] bg-[#F5F5F5] p-2">
              <WebpImg
                src={productImageState[0]?.url}
                className="h-full w-full"
                alt=""
                width={410}
              />
            </div>
            <div className="ml-[12px] flex flex-col justify-between space-y-2">
              <div className="text-[14px] font-[400] text-[#4A4A4A]">
                {productBrandState?.name}
              </div>
              <div className="text-[18px] font-[500]">{productNameState}</div>
            </div>
          </div>
          <div className="py-[32px]">
            {productFactsState.map((item) => {
              // find from appLaunch item
              const factContent = arrProductFactsState.find(
                (items) => items.id === item.id
              );

              // render data according to id (render all or filter necessary)
              const displayContent =
                item.id === 1
                  ? factContent?.items.map((items) => items)
                  : factContent?.items.filter((items) => {
                      return item?.valid_items.some((f) => {
                        return f === items.id;
                      });
                    });

              return (
                <div className="border-t-[1px] border-gray-300 py-[16px]">
                  <ItemCategory
                    title={item?.name}
                    textColor={item?.text_color}
                    bgColor={item?.bg_color}
                    className="flex w-auto"
                  />
                  {factContent?.description ? (
                    <div className="pt-[24px] text-[16px] font-[400]">
                      {factContent?.description}
                    </div>
                  ) : null}
                  <div className="pb-[32px]">
                    {displayContent.map((displayItem) => {
                      // check if content is exist
                      const isExist = item?.valid_items.some((f) => {
                        return f === displayItem.id;
                      });
                      return (
                        <div className="flex items-start pt-[32px]">
                          {item?.id === 2 ? (
                            <WebpImg
                              src={displayItem?.images[0]?.url}
                              className="mr-[12px] mt-[6px] h-[48px] w-[48px]"
                              width={48}
                            />
                          ) : (
                            <StrapiImg
                              src={
                                isExist
                                  ? '/svgs/check-circle.svg'
                                  : '/svgs/x-circle.svg'
                              }
                              className="mr-[12px] mt-[6px] h-[20px] w-[20px]"
                            />
                          )}
                          <div>
                            <div className="text-[18px] font-medium">
                              {displayItem?.name}
                            </div>
                            <div>{displayItem?.description}</div>
                            {item?.id === 3 ? (
                              <div className="mt-[8px] flex items-center gap-x-4">
                                {displayItem?.images?.map((imageItem) => (
                                  <WebpImg
                                    src={imageItem?.url}
                                    className="h-[48px] w-[48px]"
                                    width={48}
                                  />
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileFactTagPopup(props) {
  const {
    isShow,
    setIsShow,
    productNameState,
    productBrandState,
    productImageState,
    arrProductFactsState,
    currentFactState,
    productFactsState,
  } = props;

  function ItemCategory(props) {
    const { title, textColor, bgColor } = props;
    return (
      <div
        style={{ color: textColor, background: bgColor }}
        className={`w-fit rounded-[6px] px-[8px] py-[4px] text-[12px] font-[500]`}
      >
        {title}
      </div>
    );
  }
  const closePopup = () => {
    setIsShow(false);
  };

  if (!isShow) return null;

  return (
    <div className="fixed top-0 z-[99] flex h-full w-full flex-col overflow-y-scroll bg-[#fff] lg:hidden">
      <div className="relative mx-[16px] mt-[16px] flex h-[48px] flex-row items-center justify-between">
        <div className="flex items-center justify-center">
          <div
            className="absolute left-0 top-0 flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded-[8px] border-[1px] border-[#E7E7E7]"
            onClick={closePopup}
          >
            <StrapiImg src="/svgs/close.svg" className="h-[30px] w-[30px]" />
          </div>
        </div>
      </div>
      <div className="mx-[16px] mb-[16px] mt-[32px] flex-grow overflow-scroll">
        <div className="flex items-center">
          <div className="flex h-[80px] w-[80px] items-center justify-center rounded-[16px] bg-[#F5F5F5] p-2">
            <WebpImg
              width={80}
              src={productImageState[0]?.url}
              className="h-full w-full"
              alt=""
            />
          </div>
          <div className="ml-[12px] flex flex-col justify-between space-y-2">
            <div className="text-[14px] font-[400] text-[#4A4A4A]">
              {productBrandState?.name}
            </div>
            <div className="text-[18px] font-[500]">{productNameState}</div>
          </div>
        </div>
        <div className="py-[32px]">
          {productFactsState.map((item) => {
            // find from appLaunch item
            const factContent = arrProductFactsState.find(
              (items) => items.id === item.id
            );

            // render data according to id (render all or filter necessary)
            const displayContent =
              item.id === 1
                ? factContent?.items.map((items) => items)
                : factContent?.items.filter((items) => {
                    return item?.valid_items.some((f) => {
                      return f === items.id;
                    });
                  });

            return (
              <div className="border-t-[1px] border-gray-300 py-[16px]">
                <ItemCategory
                  title={item?.name}
                  textColor={item?.text_color}
                  bgColor={item?.bg_color}
                  className="flex w-auto"
                />
                {factContent?.description ? (
                  <div className="pt-[24px] text-[16px] font-[400]">
                    {factContent?.description}
                  </div>
                ) : null}
                <div className="pb-[32px]">
                  {displayContent.map((displayItem) => {
                    // check if content is exist
                    const isExist = item?.valid_items.some((f) => {
                      return f === displayItem.id;
                    });
                    return (
                      <div className="flex items-start pt-[32px]">
                        {item?.id === 2 ? (
                          <WebpImg
                            src={displayItem?.images[0]?.url}
                            className="mr-[12px] mt-[6px] h-[48px] w-[48px]"
                            width={48}
                          />
                        ) : (
                          <StrapiImg
                            src={
                              isExist
                                ? '/svgs/check-circle.svg'
                                : '/svgs/x-circle.svg'
                            }
                            className="mr-[12px] mt-[6px] h-[20px] w-[20px]"
                          />
                        )}
                        <div>
                          <div className="text-[18px] font-medium">
                            {displayItem?.name}
                          </div>
                          <div>{displayItem?.description}</div>
                          {item?.id === 3 ? (
                            <div className="mt-[8px] flex items-center gap-x-4">
                              {displayItem?.images?.map((imageItem) => (
                                <WebpImg
                                  src={imageItem?.url}
                                  className="h-[48px] w-[48px]"
                                  width={48}
                                />
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const BeautyAwardRankItem = ({ item }) => {
  const { year, rank, name, badge, key } = item;
  const textColor =
    rank === 1
      ? '#E29200'
      : rank == 2
        ? '#4A4A4A'
        : rank == 3
          ? '#C8764E'
          : '#FF6079';
  const href =
    String(year) === '2025' && key
      ? `/beautyawards2025/result/ranking#${key}`
      : null;

  const content = (
    <div className="flex flex-row items-center gap-x-1">
      <div className="flex h-[70px] w-[80px] shrink-0 items-center justify-center lg:h-[85px] lg:w-[97px]">
        <WebpImg
          src={badge}
          width={300}
          className="aspect-[86/75] w-full object-contain"
        />
      </div>
      <p
        style={{ color: textColor }}
        className="grow text-sm font-normal lg:text-base lg:font-medium"
      >
        {`${year}年Sorra年度票選大賞 ${name}類 第${rank}位`}
      </p>
    </div>
  );

  if (href) {
    return (
      <a href={href} className="cursor-pointer">
        {content}
      </a>
    );
  }
  return content;
};

const MonthlyRankItem = ({ item }) => {
  const { year, month, date, rank, category, categoryId } = item;
  const textColor =
    rank === 1
      ? '#E29200'
      : rank == 2
        ? '#4A4A4A'
        : rank == 3
          ? '#C8764E'
          : '#FF6079';
  const href = categoryId ? `/monthlyRanking?category=${categoryId}` : null;

  const content = (
    <div className="flex flex-row items-center gap-x-1">
      <div className="hidden h-[85px] w-[97px] shrink-0 items-center justify-center lg:flex">
        <ProductRankBadge
          rank={rank}
          year={year}
          month={`${month}月`}
          categoryName={category}
          width={71}
        />
      </div>
      <div className="flex h-[70px] w-[80px] shrink-0 items-center justify-center lg:hidden">
        <ProductRankBadge
          rank={rank}
          year={year}
          month={`${month}月`}
          categoryName={category}
          width={62}
        />
      </div>
      <p
        style={{ color: textColor }}
        className="grow text-sm font-normal lg:text-base lg:font-medium"
      >
        {`${date}${category}類月榜 第${rank}位`}
      </p>
    </div>
  );

  if (href) {
    return (
      <a href={href} className="cursor-pointer">
        {content}
      </a>
    );
  }
  return content;
};

export default function ProductDetail(props) {
  const { seo, votingRanks } = props;

  const { getMonthlyRanks } = useAppLaunch();
  const router = useRouter();
  const {
    user,
    getAuthorizationHeader,
    isSignInStatusReady,
    userProductCollection,
    toggleUserProductCollection,
  } = useUserAuth();

  const {
    notifications,
    productFacts,
    stores,
    productOriginMap,
    storeMap,
    factorMap,

    ingredientSafeties,
    ingredientSafetyMap,
    ingredientFeatures,
  } = useLaunchData();

  const { asPath, isReady } = router;

  const { itemID } = router.query;

  const monthlyRanks = isReady ? getMonthlyRanks(_.toNumber(itemID)) : null;

  const expandAffiliateRef = useRef(null);

  // --- 立即載入：基本產品資料 ---
  const {
    data: productBase,
    mutate: mutateProductBase,
    isLoading: isProductBaseLoading,
  } = useSWR(
    isReady && isSignInStatusReady ? `/api/v1/products/${itemID}` : null,
    (url) => {
      return swrGoFetcher(url, getAuthorizationHeader);
    },
    {
      fallbackData: {},
    }
  );

  const { data: productPageBanner } = useSWR(
    isReady && isSignInStatusReady ? `/api/v5/product/${itemID}/banner` : null,
    (url) => swrFetcher(url),
    {
      fallbackData: null,
    }
  );

  // --- 立即載入：評分資料 ---
  const { data: productRatingData } = useSWR(
    isReady && isSignInStatusReady ? `/api/v1/products/${itemID}/rating` : null,
    (url) => swrGoFetcher(url),
    {
      fallbackData: {},
    }
  );

  // --- 滾到才載入：成份分析 ---
  const [ingredientsRef, ingredientsInView] = useInView();
  const { data: productIngredients } = useSWR(
    isReady && ingredientsInView
      ? `/api/v1/products/${itemID}/ingredients`
      : null,
    (url) => swrGoFetcher(url),
    {
      fallbackData: {},
    }
  );

  // --- 滾到才載入：小編實測 ---
  const [premiumRef, premiumInView] = useInView();
  const { data: productPremium } = useSWR(
    isReady && premiumInView ? `/api/v1/products/${itemID}/premium` : null,
    (url) => swrGoFetcher(url),
    {
      fallbackData: {},
    }
  );

  // --- 立即載入：真實讀者評價 ---
  const { data: productReviews } = useSWR(
    isReady && isSignInStatusReady ? `/api/v1/products/${itemID}/review` : null,
    (url) => swrGoFetcher(url, getAuthorizationHeader),
    {
      fallbackData: [],
    }
  );

  // --- 用家實拍：沒有照片就不顯示入口，避免連到空頁面 ---
  const { data: photoAvailability } = useSWR(
    isReady ? `/api/v1/products/photo-availability?ids=${itemID}` : null,
    (url) => swrGoFetcher(url)
  );
  const hasProductPhotos = photoAvailability?.items?.[0]?.has_photos === true;
  const photoCount = photoAvailability?.items?.[0]?.photo_count;

  // --- 滾到才載入：真實照片。沒照片就不打，省一次請求 ---
  const [photosRef, photosInView] = useInView();
  const { data: productPhotos } = useSWR(
    isReady && photosInView && hasProductPhotos
      ? `/api/v1/products/${itemID}/photos?type=all&page=1`
      : null,
    (url) => swrGoFetcher(url)
  );
  const previewPhotos = (productPhotos?.items || []).slice(0, 4);

  // --- 滾到才載入：KOL 評價 ---
  const [kolsRef, kolsInView] = useInView();
  const { data: productKols } = useSWR(
    isReady && kolsInView ? `/api/v1/products/${itemID}/kols` : null,
    (url) => swrGoFetcher(url),
    {
      fallbackData: [],
    }
  );

  // --- 滾到才載入：相似產品 ---
  const [relatedRef, relatedInView] = useInView();
  const { data: productRelated, isLoading: isRelatedLoading } = useSWR(
    isReady && relatedInView ? `/api/v1/products/${itemID}/related` : null,
    (url) => swrGoFetcher(url),
    {
      fallbackData: [],
    }
  );

  // 合併為 productOverview（向下相容子元件）
  const productOverview = {
    ...productBase,
    ...productRatingData,
    ...productIngredients,
    ...productPremium,
    reviews: Array.isArray(productReviews) ? productReviews : [],
    kols: Array.isArray(productKols) ? productKols : [],
    related_products: Array.isArray(productRelated) ? productRelated : [],
  };
  const mutateProductOverview = mutateProductBase;

  const isPageLoading = isProductBaseLoading;

  const { data: productAffiliates } = useSWR(
    productBase && productBase.brand
      ? `/api/v1/brands/${productBase.brand.id}/affiliate`
      : null,
    swrGoFetcher,
    {
      fallbackData: [],
    }
  );

  const productAffiliatesLength = productAffiliates.length;
  const name = productBase.name || '';
  const enName = productBase.en_name || '';
  const brand = productBase.brand;
  const description = productBase.description;
  const images = productBase.images || [];
  const kols = Array.isArray(productKols) ? productKols : [];
  const reviewAlt = `${brand?.name} ${name} 真實評價` || '';
  const kolsLength = Array.isArray(kols) ? kols.length : 0;

  const origin = productBase.origin;
  const variation = productBase.variation;
  const ingredientOverview = productIngredients.ingredient_overview;
  const productSummary = productBase.product_summary;
  const commonQuestion = productBase.common_question;
  const testingDetail = productPremium.testing_detail?.length
    ? productPremium.testing_detail
    : null;
  const testingOverview = Array.isArray(productPremium.overview)
    ? productPremium.overview.length
      ? productPremium.overview[0]
      : null
    : productPremium.overview || null;
  const premiumContent = Array.isArray(productPremium.premium_content)
    ? productPremium.premium_content.length
      ? productPremium.premium_content[0]
      : null
    : productPremium.premium_content || null;

  //// move from useEffect
  var ratingState = '-';
  var ratingRateState = '暫無';
  const rating = productRatingData.rating;
  // var rating = '';
  if (!_.isEmpty(rating) && rating.total != 0) {
    var ratingText = '';
    var ratingRateText = '';
    var ratingPercentage = (rating.positive / rating.total) * 100;
    var ratingCount =
      productRatingData.review_count || productBase.review_count; //productReviewCountState;
    var isRatingNeutral = rating.neutral == rating.total;

    if (ratingPercentage < 49) {
      ratingText += '🤔';
    } else if (ratingPercentage < 79) {
      ratingText += '👌';
    } else if (ratingCount == 0) {
      ratingText = '';
    } else {
      ratingText += '❤️';
    }

    if (ratingCount == 0) {
      ratingText += '-';
    } else {
      ratingText += ' ' + Math.round(ratingPercentage) + '% ';
      ratingText += '(' + ratingCount + ')';
    }

    if (ratingCount == 0) {
      ratingRateText += '暫無';
    } else if (ratingPercentage == 100) {
      ratingRateText = '一致向好';
    } else if (ratingPercentage >= 60) {
      ratingRateText += '大致向好';
    } else if (ratingPercentage >= 50) {
      ratingRateText += '好壞參半';
    } else if (ratingPercentage >= 1) {
      ratingRateText += '一般';
    } else if (isRatingNeutral) {
      ratingRateText += '中性';
    } else {
      ratingRateText += '一致向差';
    }

    ratingState = ratingText;
    ratingRateState = ratingRateText;
  }
  ////

  const [currentAffiliateIndex, setCurrentAffiliateIndex] = useState(0);
  const [expandAffiliateList, setExpandAffiliateList] = useState(false);
  const [categoryNameState, setCategoryNameState] = useState('');
  useEffect(() => {
    setCurrentAffiliateIndex(0);
  }, [productAffiliatesLength]);

  const [isReportModalShow, setIsReportModalShow] = useState(false);

  const [blockInfluencerState, setBlockInfluencerState] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [width, setWidth] = useState(0);
  const isMobile = width <= 768;
  const [isShowFactPopup, setIsShowFactPopup] = useState(false);

  const [currentFactState, setCurrentFactState] = useState(1);

  const [isCommentModalShow, setIsCommentModalShow] = useState(false);

  const targetSafety = ingredientSafetyMap[ingredientOverview?.safety];
  // ingredientSafeties ?  arrIngridentSafetyState.find(
  //   (x) => x.id === ingridentSafetyState
  // );
  const safetyName = targetSafety?.name;
  const prodCategory = _.head(productBase.second_category)
    ? _.head(productBase.second_category).name
    : '';

  // const targetSkinType = arrFactorTypesState[0]?.factors?.find(
  //   (x) => x.id === productRatingState?.factor
  // );
  // console.log({ targetSkinType });
  const targetSkinType = rating ? factorMap[rating.factor] : null;
  const skinTypeName = targetSkinType?.name ?? '所有';

  const [isReviewPopupShow, setIsReviewPopupShow] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [reviewUser, setReviewUser] = useState(null);
  const [imageList, setImageList] = useState([]);

  // const pathname = usePathname();

  useEffect(() => {
    if (!_.isEmpty(productBase)) {
      const category = _.head(productBase.second_category)
        ? _.head(productBase.second_category).name
        : null;
      setCategoryNameState(category);
    }
  }, [productBase]);

  const { data: posts, isLoading: isPostsLoading } = useSWR(
    categoryNameState != ''
      ? '/v1/wordpress/posts?page=1&per_page=4&search=' + categoryNameState
      : null,
    swrSorraAPIFetcher,
    { fallbackData: [] }
  );
  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
    // console.log('window', window.innerWidth)
  }

  useEffect(() => {
    if (isSignInStatusReady && user) {
      const queryParameters = new URLSearchParams(window.location.search);
      const leaveComment = queryParameters.get('leaveComment');
      if (leaveComment != undefined) {
        setIsCommentModalShow(true);
      }
    }
  }, [user, isSignInStatusReady]);

  function loadInfluencerContent() {
    var blockInfluencerTmp = [];
    const kols = Array.isArray(productKols) ? productKols : [];
    const targetItemCount =
      user && kols.length > 4 ? 4 : !user && kols.length > 4 ? 3 : kols.length;

    // const router = useRouter()
    // const { asPath } = router
    // console.log({ arrInfluencerState });

    if (kols.length == 0) {
      blockInfluencerTmp.push(
        <div
          className="max-h-[241px] lg:max-h-[405px] lg:w-[304px]"
          key="empty-state"
        >
          <div className="hidden w-full items-center justify-center rounded-xl object-cover lg:flex">
            <StrapiImg src="/images/chat_empty.png" />
          </div>
          <div className="flex items-center gap-x-2 pt-0 text-sm text-sorra-gray-800 lg:pt-6">
            <StrapiImg
              src="/svgs/chat-small.svg"
              width={20}
              height={20}
              alt=""
            />
            其他評論
          </div>
          <div className="line-clamp-2 pt-1 text-[16px] font-[400] text-sorra-gray-900">
            427 likes, 1 comments - sorra.beauty on...
          </div>
        </div>
      );
    } else {
      for (let index = 0; index < targetItemCount; index += 1) {
        const element = kols[index];
        const metaData = element?.meta;
        const metaSite = metaData?.site_name;
        const targetIcon =
          metaSite === 'YouTube'
            ? '/svgs/youtube.svg'
            : metaSite === 'Instagram'
              ? '/svgs/instagram.svg'
              : '/svgs/chat-small.svg';

        blockInfluencerTmp.push(
          <div key={element.id}>
            <div
              className="hidden cursor-pointer lg:block lg:h-[241px] lg:w-[304px]"
              onClick={() => {
                if (user) {
                  window.open(element?.url, '_blank');
                } else {
                  redirectToLogin(asPath);
                }
              }}
            >
              {metaData?.image?.url ? (
                <div className="relative h-[170px] w-full rounded-xl object-cover">
                  <WebpImg
                    className="h-full w-full rounded-xl object-cover"
                    src={
                      metaData?.image?.url == ''
                        ? '/images/chat_empty.png'
                        : metaData?.image?.url
                    }
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = '/images/chat_empty.png';
                    }}
                  />
                  {!user ? (
                    <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center rounded-lg bg-black/30 text-white">
                      <StrapiImg
                        className={`h-[61px] w-[61px] ${
                          !user ? 'noselect' : ''
                        }`}
                        src="/svgs/lock-open-white.svg"
                      ></StrapiImg>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="hidden w-full items-center justify-center rounded-xl object-cover lg:flex">
                  <StrapiImg src="/images/chat_empty.png" />
                </div>
              )}
              <div className="mt-[16px] flex w-full items-center gap-x-4 truncate">
                <div className="w-full">
                  <div className="flex items-center text-[16px] font-[400]">
                    {
                      <StrapiImg
                        src={targetIcon}
                        className={`mr-[4px] h-[20px] w-[20px] ${
                          !user ? 'noselect' : ''
                        }`}
                        alt=""
                      />
                    }
                    <span className={`truncate ${!user ? 'noselect' : ''}`}>
                      {metaData?.title ?? '其他評論'}
                    </span>
                  </div>
                  <div className="text-[16px] font-[400] text-[#4A4A4A]">
                    {!user && index > 1 ? (
                      <Skeleton height={24} />
                    ) : (
                      <div
                        className={`truncate text-[16px] font-[400] text-[#4A4A4A] ${
                          !user ? 'noselect' : ''
                        }`}
                      >
                        {metaData?.description ??
                          '427 likes, 1 comments - sorra.beauty on...'}
                      </div>
                    )}
                  </div>
                </div>
                {/* <StrapiImg className="grow-0 w-[24px] h-[24px]" src="/svgs/chevron-right.svg" alt="" /> */}
              </div>
            </div>

            <div
              className="block lg:hidden"
              onClick={() => {
                if (user) {
                  window.open(element?.url, '_blank');
                } else {
                  redirectToLogin(asPath);
                }
              }}
            >
              <div className="w-full">
                {metaData?.image?.url ? (
                  <div className="relative h-[170px] w-full rounded-xl object-cover">
                    <WebpImg
                      className="h-full w-full rounded-xl object-cover"
                      src={
                        metaData?.image?.url == ''
                          ? '/images/chat_empty.png'
                          : metaData?.image?.url
                      }
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null;
                        currentTarget.src = '/images/chat_empty.png';
                      }}
                    />
                    {!user ? (
                      <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center rounded-lg bg-black/30 text-white">
                        <StrapiImg
                          className={`h-[61px] w-[61px] ${
                            !user ? 'noselect' : ''
                          }`}
                          src="/svgs/lock-open-white.svg"
                        ></StrapiImg>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="hidden w-full items-center justify-center rounded-xl object-cover lg:flex">
                    <StrapiImg src="/images/chat_empty.png" />
                  </div>
                )}
                <div className="mt-[16px] flex w-full items-center space-x-4">
                  <div className="w-[calc(100%-40px)]">
                    <div className="flex w-full items-center text-[18px] font-[500]">
                      <div className="truncate">
                        {targetIcon !== '' ? (
                          <StrapiImg
                            src={targetIcon}
                            className={`mr-[4px] h-[20px] w-[20px] ${
                              !user ? 'noselect' : ''
                            }`}
                            alt=""
                          />
                        ) : null}
                      </div>
                      <div
                        className={`w-[calc(100%-24px)] truncate ${
                          !user ? 'noselect' : ''
                        }`}
                      >
                        {metaData?.title ?? '其他評論'}
                      </div>
                    </div>
                    <div className="mt-[8px]">
                      {!user && index > 1 ? (
                        <Skeleton height={24} />
                      ) : (
                        <div
                          className={`truncate text-[16px] font-[400] text-[#4A4A4A] ${
                            !user ? 'noselect' : ''
                          }`}
                        >
                          {metaData?.description ??
                            '427 likes, 1 comments - sorra.beauty on...'}
                        </div>
                      )}
                    </div>
                  </div>
                  <StrapiImg
                    className="h-[24px] w-[24px]"
                    src="/svgs/chevron-right.svg"
                    alt=""
                  />
                </div>
              </div>
            </div>
          </div>
        );

        if (index === 1 && !user) {
          blockInfluencerTmp.push(
            <div key="no-user">
              <div className="hidden h-[241px] lg:block">
                <div
                  className="flex h-[170px] w-full items-center justify-center rounded-xl"
                  style={{
                    backgroundImage: `linear-gradient(to right, #FFF4E7, #FFEAEA`,
                  }}
                >
                  <StrapiImg
                    src="/svgs/unlock-influencer.svg"
                    className="h-[80px] w-[87px]"
                  />
                </div>
                <div className="mt-[16px] truncate">
                  <div className="flex items-center text-[14px] font-[500]">
                    <StrapiImg
                      src="/svgs/lock-open.svg"
                      className="mr-[4px] h-4 w-4"
                    />
                    成為會員來解鎖全部 KOL 評論
                  </div>
                </div>
              </div>
              <div className="block lg:hidden">
                <div
                  className="w-full rounded-lg px-[16px] py-[16px] lg:px-[24px]"
                  style={{
                    backgroundImage: `linear-gradient(to right, #FFF4E7, #FFEAEA`,
                  }}
                >
                  <div className="flex">
                    <div className="flex grow items-center text-base font-bold text-[#111928]">
                      <StrapiImg
                        className="mr-[4px]"
                        src="/svgs/lock-open.svg"
                      ></StrapiImg>
                      成為會員來解鎖全部 KOL 評論
                    </div>
                    <button
                      className="flex h-[41px] flex-row items-center justify-center rounded-[8px] bg-sorra-pink px-[20px] py-[10px] text-sm font-medium text-[#fff]"
                      onClick={() => {
                        MixpanelConfig.track(
                          'signinup_from_product_contentblock'
                        );
                        redirectToLogin(asPath);
                      }}
                    >
                      成為會員
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }
      }
    }

    setBlockInfluencerState(blockInfluencerTmp);
  }

  async function pressLike(isMobile) {
    // const auth = firebaseAuth;
    // const user = auth.currentUser;

    if (user == null) {
      redirectToLogin(asPath);
    } else {
      try {
        await toggleUserProductCollection(productOverview.id);
        if (isMobile) {
          if (collected_toast_mobile.classList.contains('hidden')) {
            collected_toast_mobile.classList.remove('hidden');

            window.setTimeout(function () {
              collected_toast_mobile.classList.add('hidden');
            }, 5000);
          }
        } else {
          if (collected_toast_desktop.classList.contains('lg:hidden')) {
            collected_toast_desktop.classList.remove('hidden');
            collected_toast_desktop.classList.remove('lg:hidden');

            window.setTimeout(function () {
              collected_toast_desktop.classList.add('hidden');
              collected_toast_desktop.classList.add('lg:hidden');
            }, 5000);
          }
        }
      } catch (e) {
        console.log(e);
      }
    }
    //   const token = user.accessToken;
    //   const passObject = JSON.stringify({ product_id: router.query.itemID });

    //   try {
    //     const [success, error, statusCode, data] = await httpCall(
    //       'post',
    //       process.env.NEXT_PUBLIC_API_HOST + '/api/v2/me/products',
    //       token,
    //       passObject
    //     );

    //     if (success == true) {
    //       if (statusCode == 200) {
    //         // console.log('success');

    //         const collected_toast_desktop = document.getElementById(
    //           'collected_toast_desktop'
    //         );
    //         const collected_toast_mobile = document.getElementById(
    //           'collected_toast_mobile'
    //         );

    //         // console.log({ isMobile, collected_toast_desktop });

    //         if (isMobile) {
    //           if (collected_toast_mobile.classList.contains('hidden')) {
    //             collected_toast_mobile.classList.remove('hidden');

    //             window.setTimeout(function () {
    //               collected_toast_mobile.classList.add('hidden');
    //             }, 5000);
    //           }
    //         } else {
    //           if (collected_toast_desktop.classList.contains('lg:hidden')) {
    //             collected_toast_desktop.classList.remove('hidden');
    //             collected_toast_desktop.classList.remove('lg:hidden');

    //             window.setTimeout(function () {
    //               collected_toast_desktop.classList.add('hidden');
    //               collected_toast_desktop.classList.add('lg:hidden');
    //             }, 5000);
    //           }
    //         }

    //         mutateProductOverview();
    //         // fetchProductDetails();
    //       }
    //     } else {
    //       console.log('api error:', error);
    //     }
    //   } catch (error) {
    //     console.log('api error:', error);
    //   }
    // }
  }

  async function pressUnlike() {
    // const auth = firebaseAuth;
    // const user = auth.currentUser;
    if (user == null) {
      redirectToLogin(asPath);
    } else {
      try {
        await toggleUserProductCollection(productOverview.id);
      } catch (e) {
        console.log(e);
      }
      // const token = user.accessToken;

      // try {
      //   const [success, error, statusCode, data] = await httpCall(
      //     'delete',
      //     process.env.NEXT_PUBLIC_API_HOST +
      //       '/api/v2/me/products/' +
      //       router.query.itemID,
      //     token,
      //     null
      //   );

      //   if (success == true) {
      //     if (statusCode == 200) {
      //       // console.log('success');
      //       // fetchProductDetails();
      //     }
      //     mutateProductOverview();
      //   } else {
      //     console.log('api error:', error);
      //   }
      // } catch (error) {
      //   console.log('api error:', error);
      // }
    }
  }

  useEffect(() => {
    // checkSignIn();
    setWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    loadInfluencerContent();
  }, [user, kolsLength]);

  function pressShareDesktop(product) {
    var brandName = '';
    var productName = '';

    if (product.brand != null) {
      brandName = product.brand.name;
    }

    productName = product.name;

    var copyText =
      brandName +
      ' - ' +
      productName +
      ' | Sorra 美妝評論網站\nhttps://www.sorra.net/product/' +
      product.id;

    navigator.clipboard.writeText(copyText);

    const search_share_toast_desktop = document.getElementById(
      'search_share_toast_desktop'
    );

    if (search_share_toast_desktop.classList.contains('hidden')) {
      search_share_toast_desktop.classList.remove('hidden');

      window.setTimeout(function () {
        search_share_toast_desktop.classList.add('hidden');
      }, 5000);
    }
  }

  function pressShareMobile(product) {
    // console.log('pressShareMobile', product)
    var brandName = '';
    var productName = '';

    if (product.brand != null) {
      brandName = product.brand.name;
    }

    productName = product.name;

    if (navigator.share) {
      navigator
        .share({
          title:
            brandName +
            ' - ' +
            productName +
            ' | Sorra 美妝評論網站\nhttps://www.sorra.net/product/' +
            product.id,
          text: brandName + ' - ' + productName + ' | Sorra 美妝評論網站',
          url: 'https://www.sorra.net/product/' + product.id,
        })
        .then(() => {
          const search_share_toast_mobile = document.getElementById(
            'search_share_toast_mobile'
          );

          if (search_share_toast_mobile.classList.contains('hidden')) {
            search_share_toast_mobile.classList.remove('hidden');

            window.setTimeout(function () {
              search_share_toast_mobile.classList.add('hidden');
            }, 5000);
          }
        });
    }
  }
  return (
    <div>
      <NextSeo
        title={seo.title}
        description={seo.description}
        noindex={seo.noindex}
        canonical={nextSeoConfig.canonical + 'product/' + seo.id}
        openGraph={{
          title: seo.title,
          description: seo.description,
          url: nextSeoConfig.canonical + 'product/' + seo.id,
          type: 'website',
          images: [
            {
              url: `${nextSeoConfig.canonical}api/og/product/${seo.id}`,
              width: 1200,
              height: 630,
              alt: seo.name,
            },
          ],
        }}
      />
      {seo.reviewCount !== '0' && (
        <ProductJsonLd
          productName={seo.name}
          alternateName={seo.alternateName || undefined}
          brand={seo.brand}
          description={seo.description}
          images={
            seo.images && seo.images.length > 0
              ? seo.images
              : '/images/placeholders/product.png'
          }
          aggregateRating={{
            ratingValue: seo.ratingValue,
            reviewCount: seo.reviewCount,
            worstRating: '0',
            bestRating: '5',
          }}
          category={seo.category ? seo.category : null}
          countryOfOrigin={seo.origin ? seo.origin : null}
          reviews={seo.reviews ? seo.reviews : null}
          offers={seo.offers && seo.offers?.length > 0 ? seo.offers : null}
          additionalProperty={seo.ingredient ? seo.ingredient : null}
          sku={seo.variation ? seo.variation : null}
        />
      )}
      {seo.faqs.length > 0 && <FAQPageJsonLd mainEntity={seo.faqs} />}
      {seo.breadCrumb.length > 0 && (
        <BreadcrumbJsonLd itemListElements={seo.breadCrumb} />
      )}
      {isReviewPopupShow ? (
        <div
          className="fixed bottom-0 left-0 right-0 top-0 z-50 bg-black lg:bg-sorra-gray-800/70"
          onClick={() => setIsReviewPopupShow(false)}
        >
          <div
            className="absolute left-4 top-4 block lg:hidden"
            onClick={() => setIsReviewPopupShow(false)}
          >
            <StrapiImg src="/svgs/close-white.svg" className="h-4 w-4" />
          </div>

          <div className="flex h-full items-center justify-center">
            <div className="relative flex flex-col items-start justify-center">
              <div
                className="absolute left-4 top-[-36px] hidden cursor-pointer lg:block"
                onClick={() => setIsReviewPopupShow(false)}
              >
                <StrapiImg src="/svgs/close-white.svg" className="h-4 w-4" />
              </div>
              <div className="flex w-full items-center justify-center gap-x-4">
                {selectedImageIndex > 0 ? (
                  <StrapiImg
                    src="/svgs/arrow-left.svg"
                    width={40}
                    height={40}
                    className="hidden cursor-pointer lg:block"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(selectedImageIndex - 1);
                    }}
                  />
                ) : (
                  <div className="hidden h-10 w-10 lg:block" />
                )}
                <WebpImg
                  alt={
                    reviewAlt +
                    '/實測照片 by ' +
                    reviewUser +
                    ' ' +
                    selectedImageIndex
                  }
                  title={
                    reviewAlt +
                    '/用後感 by ' +
                    reviewUser +
                    ' ' +
                    selectedImageIndex
                  }
                  src={imageList[selectedImageIndex].url}
                  width={600}
                  className="max-h-[600px] w-full overflow-hidden object-contain lg:max-h-[600px] lg:w-auto lg:rounded-2xl"
                />
                {selectedImageIndex + 1 != imageList.length ? (
                  <StrapiImg
                    src="/svgs/arrow-right.svg"
                    width={40}
                    height={40}
                    className="hidden cursor-pointer lg:block"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(selectedImageIndex + 1);
                    }}
                  />
                ) : (
                  <div className="hidden h-10 w-10 lg:block" />
                )}
              </div>
              <div className="flex items-center justify-center space-x-2 px-[16px] pt-[24px] lg:px-[56px]">
                {imageList.map((item, index) => (
                  <WebpImg
                    alt={reviewAlt + '/實測照片 by ' + reviewUser + ' ' + index}
                    title={reviewAlt + '/用後感 by ' + reviewUser + ' ' + index}
                    src={getFormattedProductImageUrl(item)}
                    className={`h-[40px] w-[40px] cursor-pointer rounded object-cover ${
                      index == selectedImageIndex
                        ? 'border-[2px] border-white'
                        : ''
                    }`}
                    width={40}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageIndex(index);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <main>
        <Menubar
          props={{
            homeExtend: false,
            sourceUI: 'Product',
          }}
        />

        <div
          className={
            'globals-centerContentsMargin' +
            (notifications.length > 0 ? ' withNoti' : '')
          }
        >
          <MaxWidthContainer className="px-4">
            <div
              className={`flex w-full flex-wrap items-center gap-1 py-[16px] lg:py-[32px]`}
            >
              首頁 &gt;{' '}
              <span
                className="cursor-pointer"
                onClick={() =>
                  window.open(
                    `/search?product=&category=${_.head(productBase.second_category) ? _.head(productBase.second_category).name : ''}&categoryID=${_.head(productBase.second_category) ? _.head(productBase.second_category).id : ''}&keyword=`
                  )
                }
              >
                {prodCategory}
              </span>{' '}
              &gt;{' '}
              <span
                className="cursor-pointer"
                onClick={() =>
                  window.open(`/brand/${brand && brand.id}`, '_blank')
                }
              >
                {brand ? brand.name : ''}
              </span>{' '}
              &gt; {name || enName}
            </div>
          </MaxWidthContainer>
          {isPageLoading && <LoadingSkeleton />}
          <div
            className={classnames({
              hidden: isPageLoading,
            })}
          >
            <MaxWidthContainer className="px-4">
              <div className="items-center lg:flex lg:flex-row lg:items-start">
                <div className="w-full flex-shrink-0 lg:w-[410px]">
                  <div className="relative flex flex-col items-center justify-center overflow-hidden rounded-lg border border-sorra-gray-100 bg-white">
                    <div className="relative h-[273px] lg:h-[410px]">
                      <div className="aspect-square h-full w-full">
                        <WebpImg
                          alt={
                            (brand ? brand.name : '') +
                            ' ' +
                            enName +
                            ' ' +
                            name +
                            '產品照片'
                          }
                          title={
                            (brand ? brand.name : '') +
                            ' ' +
                            enName +
                            ' ' +
                            name
                          }
                          src={getProductWebpImageUrl(images[selectedPhoto])}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex h-[64px] flex-row items-center justify-start gap-x-4 overflow-x-scroll lg:h-[112px]">
                    {images.map((item, index) => (
                      <WebpImg
                        key={index}
                        alt={
                          (brand ? brand.name : '') +
                          ' ' +
                          enName +
                          ' ' +
                          name +
                          '產品照片'
                        }
                        title={
                          (brand ? brand.name : '') + ' ' + enName + ' ' + name
                        }
                        src={getProductWebpImageUrl(item)}
                        className={`flex h-[64px] w-[64px] cursor-pointer items-center justify-center rounded-lg border object-contain lg:h-[112px] lg:w-[112px] ${
                          index === selectedPhoto
                            ? 'border-[#000]'
                            : 'border-sorra-gray-100'
                        }`}
                        onClick={() => {
                          setSelectedPhoto(index);
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="relative mt-[32px] lg:ml-[48px] lg:mt-0 lg:grow">
                  <div className="absolute right-0 top-0 flex gap-x-6"></div>
                  <h1>
                    <div className="flex items-center">
                      <div
                        className="grow cursor-pointer text-[16px] font-[400] leading-[1.5] text-[#4A4A4A]"
                        onClick={() =>
                          window.open(`/brand/${brand && brand.id}`, '_blank')
                        }
                      >
                        {brand?.name || ''}&nbsp;
                      </div>
                      <div className="flex shrink-0 items-center gap-x-6">
                        <img
                          src="/svgs/share.svg"
                          className="h-6 w-6 cursor-pointer"
                          onClick={() => {
                            if (!isMobile) {
                              pressShareDesktop(productOverview);
                            } else {
                              pressShareMobile(productOverview);
                            }
                          }}
                        />

                        <img
                          src={
                            userProductCollection.includes(productOverview.id)
                              ? '/svgs/bookmark-filled.svg'
                              : '/svgs/bookmark.svg'
                          }
                          className="h-[24px] w-[24px] cursor-pointer"
                          onClick={() => {
                            userProductCollection.includes(productOverview.id)
                              ? pressUnlike()
                              : pressLike(isMobile);
                          }}
                        ></img>
                      </div>
                    </div>
                    <div className="w-full text-lg font-medium lg:text-2xl">
                      {enName && (
                        <>
                          {enName}&nbsp; <br className="block lg:hidden" />
                        </>
                      )}
                      <div>{name}&nbsp;</div>
                    </div>
                    <div className="text-base font-normal leading-normal text-[#4A4A4A]">
                      價格比較及評價
                    </div>
                  </h1>
                  <h2 className="sr-only">
                    {prodCategory} {brand ? brand.name : ''} {name}{' '}
                  </h2>
                  <div className="mt-[8px] flex w-full flex-wrap gap-2">
                    {productOriginMap[origin]
                      ? (() => {
                          const originKey = findSingleKeyword('origin', origin);
                          const href = buildCategoryUrl(
                            originKey,
                            prodCategory
                          );
                          const tag = (
                            <ItemCategory
                              title={`${productOriginMap[origin].name}製`}
                              textColor="#F16B7C"
                              bgColor="#FCE1E580"
                              className="flex grow"
                            />
                          );
                          return href ? (
                            <a href={href} className="flex grow">
                              {tag}
                            </a>
                          ) : (
                            tag
                          );
                        })()
                      : null}
                    {(productBase.facts || []).map((item) => (
                      <ItemCategory
                        key={item.id}
                        title={item.name}
                        textColor={item.text_color}
                        bgColor={item.bg_color}
                        className="flex grow"
                        onClick={() => {
                          setIsShowFactPopup(true);
                          setCurrentFactState(item.id);
                        }}
                      />
                    ))}
                  </div>
                  {(!_.isEmpty(votingRanks) || !_.isEmpty(monthlyRanks)) && (
                    <div className="pt-4 lg:pt-6">
                      <p className="text-xs font-normal text-[#4A4A4A] lg:text-base">
                        Sorra獎項
                      </p>
                      <div className="flex flex-col">
                        {votingRanks &&
                          votingRanks.map((item, idx) => {
                            return (
                              <BeautyAwardRankItem
                                key={`votingRank_${idx}`}
                                item={item}
                              />
                            );
                          })}
                        {monthlyRanks &&
                          monthlyRanks.map((item, idx) => {
                            return (
                              <MonthlyRankItem
                                key={`monthlyRank_${idx}`}
                                item={item}
                              />
                            );
                          })}
                      </div>
                    </div>
                  )}
                  <div
                    className={
                      (productBase.nominate == true ? 'flex' : 'hidden') +
                      ' mt-[16px] text-xs font-normal text-sorra-gray-500'
                    }
                  >
                    ◎此產品資訊由用戶上傳，目前正在等待審核，以確認資料無誤。
                  </div>
                  <div className="mt-[24px] flex flex-row items-center justify-between lg:justify-start lg:space-x-12">
                    <div className="flex flex-col">
                      <div className="text-[12px] font-[400] text-[#4a4a4a] lg:text-[16px]">
                        成份分析
                      </div>
                      {safetyName ? (
                        <a
                          href="#product-analysis"
                          onClick={(e) => {
                            e.preventDefault();
                            const target =
                              document.getElementById('product-analysis');
                            if (!target) return;
                            const topBarHeight = isMobile ? 177 : 100;
                            document.querySelector('#app-wrapper')?.scrollTo({
                              top: target.offsetTop - topBarHeight,
                              behavior: 'smooth',
                            });
                          }}
                          className="flex cursor-pointer items-center text-[14px] font-[500] hover:underline lg:text-[24px]"
                        >
                          <div
                            style={{
                              background: targetSafety?.tag_color ?? '#E7E7E7',
                            }}
                            className={`mr-[8px] h-[8px] w-[8px] rounded-[50%]`}
                          />
                          <div>{safetyName}</div>
                        </a>
                      ) : (
                        <div className="flex items-center text-[14px] font-[500] lg:text-[24px]">
                          <div
                            style={{ background: '#E7E7E7' }}
                            className={`mr-[8px] h-[8px] w-[8px] rounded-[50%]`}
                          />
                          <div>未知</div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <div className="text-[12px] font-[400] text-[#4a4a4a] lg:text-[16px]">
                        較適合膚質
                      </div>
                      {(() => {
                        const factorKey = findSingleKeyword(
                          'factor',
                          rating?.factor
                        );
                        const href = buildCategoryUrl(factorKey, prodCategory);
                        return href ? (
                          <a
                            href={href}
                            className="text-[14px] font-[500] hover:underline lg:text-[24px]"
                          >
                            {skinTypeName}
                          </a>
                        ) : (
                          <div className="text-[14px] font-[500] lg:text-[24px]">
                            {skinTypeName}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="flex flex-col">
                      <div className="text-[12px] font-[400] text-[#4a4a4a] lg:text-[16px]">
                        評率: {ratingRateState}
                      </div>
                      {ratingCount > 0 ? (
                        <a
                          href={`/product/reviews/${itemID}`}
                          className="text-[14px] font-[500] hover:underline lg:text-[24px]"
                        >{`${ratingState}`}</a>
                      ) : (
                        <div className="text-[14px] font-[500] lg:text-[24px]">{`${ratingState}`}</div>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <div className="text-[12px] font-[400] text-[#4a4a4a] lg:text-[16px]">
                        官方價格
                      </div>
                      <div className="text-[14px] font-[500] lg:text-[24px]">
                        {productBase.officials &&
                        productBase.officials.length > 0
                          ? `HK$ ${productBase.officials[0].price}`
                          : '-'}
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 flex flex-col gap-x-5 gap-y-5 lg:mt-12 lg:flex-row">
                    <button
                      disabled={!user}
                      className={
                        'flex h-12 basis-auto flex-row items-center justify-center rounded-lg text-[#fff] lg:basis-1/2' +
                        (user ? ' bg-sorra-pink' : ' bg-[#FCD6DB]')
                      }
                      onClick={() => {
                        const auth = firebaseAuth;
                        const user = auth.currentUser;
                        if (!user) {
                          MixpanelConfig.track('signinup_from_product_comment');
                          redirectToLogin(asPath);
                        } else {
                          setIsCommentModalShow(true);
                        }
                      }}
                    >
                      <StrapiImg
                        src="/svgs/chat.svg"
                        className="mr-[12px] h-5 w-5"
                      />
                      留評價
                    </button>
                    {/* 沒有照片時整個入口不出現，不讓使用者點進空頁面 */}
                    {hasProductPhotos && (
                      <a
                        type="button"
                        className="flex h-12 basis-auto flex-row items-center justify-center rounded-lg border border-sorra-pink text-sorra-pink focus:ring-0 lg:basis-1/2"
                        href={`/product/${itemID}/photos`}
                      >
                        <StrapiImg
                          src="/images/icons/photo.svg"
                          className="mr-[12px] h-5 w-5"
                        />
                        查看全部 {photoCount} 張用戶實拍圖
                      </a>
                    )}
                    {productAffiliates.length > 0 && (
                      <div className="relative flex h-12 basis-auto flex-row gap-x-1.5 text-[#fff] lg:basis-1/2">
                        {productAffiliates[currentAffiliateIndex]
                          .promo_text && (
                          <div
                            style={{
                              left: `64px`,
                              width: `calc(100% - 64px  - ${productAffiliates.length > 1 ? '54px' : '0px'} )`,
                            }}
                            className="absolute top-0 flex -translate-y-1/2 justify-center"
                          >
                            <p className="rounded-lg bg-sorra-blue px-1 text-xs font-semibold leading-normal">
                              {
                                productAffiliates[currentAffiliateIndex]
                                  .promo_text
                              }
                            </p>
                          </div>
                        )}
                        <button
                          className="flex h-full grow basis-auto flex-row overflow-hidden rounded-lg border border-sorra-blue text-[#fff]"
                          onClick={() => {
                            window.open(
                              productAffiliates[currentAffiliateIndex].url ||
                                '',
                              '_blank'
                            );
                          }}
                        >
                          <div className="flex h-full w-16 flex-shrink-0 items-center justify-center bg-sorra-blue">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path
                                d="M3 1C2.73478 1 2.48043 1.10536 2.29289 1.29289C2.10536 1.48043 2 1.73478 2 2C2 2.26522 2.10536 2.51957 2.29289 2.70711C2.48043 2.89464 2.73478 3 3 3H4.22L4.525 4.222C4.52803 4.23607 4.53136 4.25007 4.535 4.264L5.893 9.694L5 10.586C3.74 11.846 4.632 14 6.414 14H15C15.2652 14 15.5196 13.8946 15.7071 13.7071C15.8946 13.5196 16 13.2652 16 13C16 12.7348 15.8946 12.4804 15.7071 12.2929C15.5196 12.1054 15.2652 12 15 12H6.414L7.414 11H14C14.1857 10.9999 14.3676 10.9481 14.5255 10.8504C14.6834 10.7528 14.811 10.6131 14.894 10.447L17.894 4.447C17.9702 4.29458 18.0061 4.12522 17.9985 3.95501C17.9908 3.78479 17.9398 3.61935 17.8502 3.47439C17.7606 3.32944 17.6355 3.20977 17.4867 3.12674C17.3379 3.04372 17.1704 3.00009 17 3H6.28L5.97 1.757C5.91583 1.54075 5.79095 1.34881 5.61521 1.21166C5.43946 1.0745 5.22293 1.00001 5 1H3ZM16 16.5C16 16.8978 15.842 17.2794 15.5607 17.5607C15.2794 17.842 14.8978 18 14.5 18C14.1022 18 13.7206 17.842 13.4393 17.5607C13.158 17.2794 13 16.8978 13 16.5C13 16.1022 13.158 15.7206 13.4393 15.4393C13.7206 15.158 14.1022 15 14.5 15C14.8978 15 15.2794 15.158 15.5607 15.4393C15.842 15.7206 16 16.1022 16 16.5ZM6.5 18C6.89782 18 7.27936 17.842 7.56066 17.5607C7.84196 17.2794 8 16.8978 8 16.5C8 16.1022 7.84196 15.7206 7.56066 15.4393C7.27936 15.158 6.89782 15 6.5 15C6.10218 15 5.72064 15.158 5.43934 15.4393C5.15804 15.7206 5 16.1022 5 16.5C5 16.8978 5.15804 17.2794 5.43934 17.5607C5.72064 17.842 6.10218 18 6.5 18Z"
                                fill="white"
                              />
                            </svg>
                          </div>
                          <div className="relative flex h-full min-w-0 grow items-center justify-center gap-x-1 overflow-hidden px-5 text-base">
                            <p className="text-black">
                              {productAffiliates[currentAffiliateIndex]
                                .image ? (
                                <WebpImg
                                  src={
                                    productAffiliates[currentAffiliateIndex]
                                      .image.url
                                  }
                                  className="h-[14px] object-contain"
                                  width={350}
                                />
                              ) : (
                                <p className="max-w-full truncate text-black">
                                  {
                                    productAffiliates[currentAffiliateIndex]
                                      .name
                                  }
                                </p>
                              )}
                            </p>
                            <p className="flex-shrink-0 text-sorra-blue">
                              優惠購！
                            </p>
                          </div>
                        </button>
                        {productAffiliates.length > 1 && (
                          <button
                            className={classnames(
                              'flex aspect-square h-full flex-shrink-0 items-center justify-center rounded-lg border border-sorra-blue hover:bg-sorra-blue hover:bg-opacity-10',
                              {
                                'bg-sorra-blue bg-opacity-10':
                                  expandAffiliateList,
                              }
                            )}
                            onClick={() => {
                              setExpandAffiliateList(!expandAffiliateList);
                            }}
                          >
                            {expandAffiliateList ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="19"
                                height="10"
                                viewBox="0 0 19 10"
                                fill="none"
                              >
                                <path
                                  d="M17.5 9L10.2071 1.70711C9.81658 1.31658 9.18342 1.31658 8.79289 1.70711L1.5 9"
                                  stroke="#5882FF"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="19"
                                height="10"
                                viewBox="0 0 19 10"
                                fill="none"
                              >
                                <path
                                  d="M1.5 1L8.79289 8.29289C9.18342 8.68342 9.81658 8.68342 10.2071 8.29289L17.5 1"
                                  stroke="#5882FF"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                />
                              </svg>
                            )}
                          </button>
                        )}
                        {expandAffiliateList &&
                          productAffiliates.length > 1 && (
                            <div className="absolute bottom-0 z-50 w-full">
                              <div
                                ref={expandAffiliateRef}
                                style={{
                                  boxShadow:
                                    '0px 4px 50px 0px rgba(0, 0, 0, 0.10)',
                                }}
                                className="border-grey-100 absolute top-1 flex w-full flex-col rounded-lg border bg-white py-2"
                              >
                                {productAffiliates.map((item, idx) => {
                                  const { name, image, url, promo_text } = item;

                                  return (
                                    <button
                                      key={item.id}
                                      className="flex items-center gap-x-1 py-4 pl-6 pr-3 hover:bg-sorra-blue hover:bg-opacity-10"
                                      onClick={() => {
                                        setCurrentAffiliateIndex(idx);
                                        setExpandAffiliateList(false);
                                        window.open(url || '', '_blank');
                                      }}
                                    >
                                      <div className="flex grow items-center gap-x-1">
                                        {image ? (
                                          <WebpImg
                                            src={image.url}
                                            className="h-[14px] object-contain"
                                            width={400}
                                          />
                                        ) : (
                                          <p className="text-black">{name}</p>
                                        )}
                                        <p className="flex-shrink-0 text-sorra-blue">
                                          優惠購！
                                        </p>
                                      </div>
                                      {promo_text && (
                                        <p className="flex-shrink-0 rounded-lg bg-sorra-blue px-1 text-xs font-bold leading-normal">
                                          {promo_text}
                                        </p>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                  {productPageBanner && (
                    <div className="mt-6 h-auto w-full">
                      <WebpImg
                        width={720}
                        src={
                          productPageBanner.image
                            ? productPageBanner.image.url
                            : ''
                        }
                        className={`h-full w-full rounded-lg lg:object-cover ${
                          productPageBanner.new_tab ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => {
                          MixpanelConfig.track(
                            'signinup_from_product_contentblock'
                          );
                          MixpanelConfig.track('Click', {
                            Component: 'Product Page Ad',
                            'Ad Name': productPageBanner.name || null,
                            'Ad URL': productPageBanner.url || null,
                          });
                          productPageBanner.new_tab && productPageBanner.url
                            ? window.open(productPageBanner.url, '_blank')
                            : null;
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </MaxWidthContainer>

            <SelectCategory
              isMobile={isMobile}
              productReviewCount={productRatingData.review_count}
              ingridentCount={
                productIngredients.ingredient_overview
                  ? productIngredients.ingredient_overview.count
                  : 0
              }
              arrInfluencerState={kols}
              arrStoresState={stores}
              summaryCount={
                productSummary?.ingredient_summary ||
                productSummary?.review_summary
                  ? 1
                  : 0
              }
              testingDetail={testingDetail}
              testingOverview={testingOverview}
              premiumContent={premiumContent}
              commonQuestion={commonQuestion}
              photoCount={hasProductPhotos ? photoCount : 0}
            />

            {productSummary?.ingredient_summary ||
            productSummary?.review_summary ? (
              <ProductSummary summary={productSummary} product_name={name} />
            ) : null}

            <div ref={premiumRef}>
              {testingDetail?.length || testingOverview || premiumContent ? (
                <TestingResult
                  testingDetail={testingDetail}
                  testingOverview={testingOverview}
                  premiumContent={premiumContent}
                />
              ) : null}
            </div>
            <AdUnit slot="7606064174" format="fluid" layout="in-article" />
            <ProductComments
              product={productOverview}
              productReviewCount={productRatingData.review_count}
              productRating={rating}
              productReviewCountState={productRatingData.review_count}
              setIsCommentModalShow={setIsCommentModalShow}
              setImageList={setImageList}
              setSelectedImageIndex={setSelectedImageIndex}
              setIsReviewPopupShow={setIsReviewPopupShow}
              setReviewUser={setReviewUser}
              countTotal={rating ? rating.total : 0}
              countPositive={rating ? rating.positive : 0}
              countNeutral={rating ? rating.neutral : 0}
              countNegative={rating ? rating.negative : 0}
              reviewAlt={reviewAlt}
              hasProductPhotos={hasProductPhotos}
            />

            {/* id 掛在外層而不是 ProductPhotos 內部：照片是滾到才載入的，
                還沒載入時內層不渲染，tab 點下去會找不到錨點 */}
            <div ref={photosRef} id="product-photos">
              <ProductPhotos
                photos={previewPhotos}
                photoCount={photoCount}
                itemID={itemID}
                productFullName={`${brand?.name || ''} ${name}`.trim()}
                onOpen={(index) => {
                  // 沿用評論照片那套放大檢視，imageList 吃 { url, formats } 就夠
                  setImageList(previewPhotos);
                  setSelectedImageIndex(index);
                  setReviewUser(previewPhotos[index]?.mask_name || '');
                  setIsReviewPopupShow(true);
                }}
              />
            </div>

            <div ref={kolsRef}>
              {(Array.isArray(kols) ? kols : []).length > 0 ? (
                <InfluencerComments
                  arrInfluencerState={kols}
                  blockInfluencerState={blockInfluencerState}
                  itemID={itemID}
                />
              ) : null}
            </div>
            <div ref={ingredientsRef}>
              {productIngredients.ingredients || [] ? (
                <ProductAnalysis
                  ingridentCount={
                    productIngredients?.ingredient_overview
                      ? productIngredients?.ingredient_overview?.count
                      : 0
                  }
                  ingridentOverviewState={
                    productIngredients.ingredient_overview || []
                  }
                  arrIngridentSafetyState={ingredientSafeties}
                  ingridentSafetyState={productIngredients.ingredient_safety}
                  arrIngredientState={productIngredients.ingredients || []}
                  isUserLoggedIn={!!user}
                  arrIngridentsFeatureState={ingredientFeatures}
                  hasIngredientProvider={
                    productIngredients.has_ingredient_provider ||
                    productBase.has_ingredient_provider ||
                    false
                  }
                  ingredientProvider={
                    productIngredients.ingredient_provider || null
                  }
                  approveIngredientProvision={
                    productBase.approve_ingredient_provision || false
                  }
                  requiresIngredientApproval={
                    productIngredients.requires_ingredient_approval ||
                    productBase.requires_ingredient_approval ||
                    false
                  }
                  ingredientImageUrls={[
                    productIngredients.ingredient_provision_cover_image_url,
                    productIngredients.ingredient_provision_image_url,
                  ]}
                  setImageList={setImageList}
                  setSelectedImageIndex={setSelectedImageIndex}
                  setIsReviewPopupShow={setIsReviewPopupShow}
                />
              ) : null}
            </div>
            <AdUnit slot="7606064174" format="fluid" layout="in-article" />
            {!stores || stores.length > 0 ? (
              <PriceCompare product={productOverview} />
            ) : null}
            <AdUnit slot="2736880877" format="auto" />
            <MoreInfo productDescriptionState={description} />
            <ProductTypes productVariationState={variation} />
            <AdUnit slot="7606064174" format="fluid" layout="in-article" />
            <Question commonQuestion={commonQuestion} />
            <MaxWidthContainer>
              <div className="mx-4 flex flex-col justify-center border-t border-gray-300 py-7 lg:mx-0 lg:py-15">
                <p className="text-center text-xs text-[#9B9B9B] lg:text-sm">
                  ◎以上資訊只供參考，詳細相關資料以實際產品標示為準。
                </p>
                <p className="text-center text-xs text-[#9B9B9B] lg:text-sm">
                  重點摘要和概覽內容由 AI 生成，如有發現錯誤，請點擊：[{' '}
                  {
                    <span
                      className="cursor-pointer underline"
                      onClick={() => {
                        if (user) {
                          setIsReportModalShow(true);
                        } else {
                          redirectToLogin(asPath);
                        }
                      }}
                    >
                      資訊錯誤回報
                    </span>
                  }{' '}
                  ]
                </p>
              </div>
            </MaxWidthContainer>
            <div ref={relatedRef}>
              <SimilarProducts
                product={productOverview}
                isLoading={isRelatedLoading}
              />
            </div>
            <AdUnit slot="2736880877" format="auto" />
            <RelatedArticle
              posts={posts}
              itemID={itemID}
              isLoading={isPostsLoading}
            />

            <div
              className="search-share-toast-desktop hidden gap-x-[8px]"
              id="search_share_toast_desktop"
            >
              <StrapiImg
                src="/svgs/check-circle-green.svg"
                className="h-[20px] w-[20px]"
              ></StrapiImg>
              <div className="text-sm text-sorra-gray-800">已複製</div>
            </div>

            <div
              className="search-share-toast-desktop hidden gap-x-[8px] lg:hidden"
              id="collected_toast_desktop"
              onClick={() => (window.location = '/profile')}
            >
              <StrapiImg
                src="/svgs/check-circle-green.svg"
                className="h-[20px] w-[20px]"
              ></StrapiImg>
              <div className="text-sm text-sorra-gray-800">
                已收藏！點擊查看列表
              </div>
            </div>

            <div
              className="search-share-toast-mobile hidden gap-x-[8px] lg:hidden"
              id="collected_toast_mobile"
              onClick={() => (window.location = '/profile')}
            >
              <StrapiImg
                src="/svgs/check-circle-green.svg"
                className="h-[20px] w-[20px]"
              ></StrapiImg>
              <div className="text-sm text-sorra-gray-800">
                已收藏！點擊查看列表
              </div>
            </div>
          </div>
        </div>

        <FactTagPopup
          isShow={isShowFactPopup}
          setIsShow={setIsShowFactPopup}
          productNameState={name}
          productBrandState={brand}
          productImageState={images}
          arrProductFactsState={productFacts}
          currentFactState={currentFactState}
          productFactsState={productBase.facts || []}
        />
        <MobileFactTagPopup
          isShow={isShowFactPopup}
          setIsShow={setIsShowFactPopup}
          productNameState={name}
          productBrandState={brand}
          productImageState={images}
          arrProductFactsState={productFacts}
          currentFactState={currentFactState}
          productFactsState={productBase.facts || []}
        />
      </main>

      <div className="pt-6 lg:pt-28"></div>

      <Footer />

      <MobileTabBar props={{ highlighted: 'home' }} />

      <CommentPopup
        externalPopupShown={isCommentModalShow}
        externalProductID={router.query.itemID}
        externalProductBrandName={brand ? brand.name : ''}
        externalProductName={name}
        setExternalPopupShown={setIsCommentModalShow}
        presetProduct={productOverview}
      />

      {isReportModalShow && (
        <ProductReportModal
          isOpen={isReportModalShow}
          setIsOpen={setIsReportModalShow}
          productOverview={productOverview}
        />
      )}
    </div>
  );
}

export async function getServerSideProps(ctx) {
  const { itemID } = ctx.params;

  if (_.isEmpty(itemID) || _.isNaN(_.toNumber(itemID))) {
    return {
      notFound: true,
    };
  }

  const cacheKey = `product:seo:${itemID}`;
  let productSeo = null;
  let cacheStatus = 'MISS';
  const isCacheEnabled = cacheService.isCacheEnabled();

  try {
    // Check if caching is enabled and try to get from cache
    if (isCacheEnabled) {
      const cachedData = cacheService.get(cacheKey);
      if (cachedData) {
        productSeo = cachedData;
        cacheStatus = 'HIT';
      }
    }

    // If not in cache, fetch from API
    if (!productSeo) {
      const { data } = await api.request({
        method: 'GET',
        url: `/api/seo/product/${itemID}`,
      });
      productSeo = data;

      // Store in cache if caching is enabled
      if (isCacheEnabled) {
        cacheService.set(cacheKey, productSeo);
      }
    }

    ctx.res.setHeader('X-Cache-Status', cacheStatus);

    const {
      productName,
      productEnName,
      brandName,
      brandID,
      title,
      description,
    } = extractProductBasics(productSeo);

    const { ratingValue, reviewCount } = calculateRatingData(productSeo);

    const images = processImages(productSeo);
    const nominate = _.get(productSeo, 'nominate', false);
    const votingRanks = _.sortBy(
      getProductOverallRanks(_.toInteger(itemID)),
      'rank'
    );

    const category = _.get(productSeo, 'second_category_name', '');
    const offers = _.get(productSeo, 'offers', []);
    const reviews = _.get(productSeo, 'reviews', []);
    const ingredientOverview = _.get(productSeo, 'ingredient_overview');
    const origin = _.get(productSeo, 'origin_name', '');
    const reviewSchema = buildReviewSchema(reviews);

    const secondCategory = _.get(productSeo, 'second_category', null);
    const breadCrumb = buildBreadCrumb(
      secondCategory,
      brandName,
      brandID,
      productName
    );

    const factor_name = _.get(productSeo, 'factor_name', '無註明');
    const ingredient = buildIngredientInfo(ingredientOverview, factor_name);
    const variation = _.get(productSeo, 'variation', '');
    const commonQuestions = _.get(productSeo, 'common_question', []);
    const faqs = buildFAQInfo(commonQuestions);

    return {
      props: {
        seo: {
          id: itemID,
          name: productName,
          alternateName: productEnName,
          brand: brandName,
          title,
          description,
          ratingValue,
          reviewCount,
          images,
          noindex: nominate,
          offers,
          ingredient,
          category,
          reviews: reviewSchema,
          origin,
          variation: variation,
          faqs: faqs,
          breadCrumb: breadCrumb,
        },
        votingRanks,
        gaBrandId: brandID,
        gaProductId: itemID,
        gaProductName: productName,
      },
    };
  } catch (error) {
    console.error('Error fetching product SEO data:', error);
    return { notFound: true };
  }
}

const extractProductBasics = (productSeo) => {
  const productName = productSeo.name || '';
  const productEnName = productSeo.en_name || '';
  const brandName = _.get(productSeo, 'brand.name', '');
  const brandID = _.get(productSeo, 'brand.id', '');
  const pageTitle = productSeo.page_title || '';
  let pageDescription =
    productSeo?.page_meta_description || productSeo?.description || '';
  pageDescription = pageDescription.replaceAll('\n', ' ').replace(/\s/g, '');
  return {
    productName: productName || productEnName,
    productEnName,
    brandName,
    brandID,
    title:
      pageTitle ||
      `${brandName} ${productName} | 價格比較及評價 | 產品推薦 | Sorra`,
    description: `${brandName} ${productName} 的真實評價、價格比較、成份資訊｜ ${pageDescription}`,
  };
};

const calculateRatingData = (productSeo) => {
  const ratingRate = _.get(productSeo, 'rating.rate', 0);
  const ratingTotal = _.get(productSeo, 'rating.total', 0);

  return {
    ratingValue: _.round(5 * (ratingRate / 100), 1).toString(),
    reviewCount: ratingTotal.toString(),
  };
};

const processImages = (productSeo) => {
  return _.get(productSeo, 'images', []).map((item) => item.url);
};

const buildReviewSchema = (reviews) => {
  const reviewSchema = [];

  reviews
    .filter((review) => review.comment)
    .forEach((review, index) => {
      const Item = {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.score > 0 ? '5' : '2',
          bestRating: '5',
        },
        author: {
          '@type': 'Person',
          name: review.mask_name || '匿名用戶',
        },
        reviewBody: review.comment,
        positiveNotes: {
          '@type': 'ItemList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: index + 1,
              name: review.comment,
            },
          ],
        },
      };
      reviewSchema.push(Item);
    });

  return reviewSchema;
};

const buildIngredientInfo = (ingredientOverview, factor_name) => {
  if (!ingredientOverview) {
    return [];
  }

  const {
    safety = '未知',
    count = 0,
    main = '未知',
    unsafe_count = 0,
  } = ingredientOverview;

  return [
    {
      '@type': 'PropertyValue',
      propertyID: 'skinType',
      name: '較適合膚質',
      value: factor_name,
    },
    {
      '@type': 'PropertyValue',
      propertyID: 'ingredients',
      name: '成份分析',
      value: `${safety} 成份表包含${count}個成分，濃度最高為 ${main}，一般作為溶劑，保濕。一般／風險成份：${unsafe_count}個。`,
    },
  ];
};

const buildFAQInfo = (faqs) => {
  if (!faqs) {
    return [];
  }

  const mainEntity = faqs.map((faq) => {
    return {
      questionName: faq.question,
      acceptedAnswerText: faq.answer,
    };
  });
  return mainEntity;
};

const buildBreadCrumb = (secondCategory, brandName, brandID, productName) => {
  const url = 'https://www.sorra.net/';
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: '首頁',
      item: url,
    },
  ];
  if (secondCategory) {
    const params = new URLSearchParams({
      product: '',
      category: secondCategory.name,
      categoryID: secondCategory.id,
      keyword: '',
    });

    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: secondCategory.name,
      item: `${url}search?${params.toString()}`,
    });
  }

  if (brandName && brandID) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: brandName,
      item: `${url}brand/${brandID}`,
    });
  }

  if (productName) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: productName,
    });
  }

  return items;
};
